import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { noMatchText } from '../lib/pageSearch';
import * as XLSX from 'xlsx';
import ResponsiblePartyField from './subs/ResponsiblePartyField';
import DateRangePicker from './DateRangePicker';
import { fmtRange } from './subs/SubMovesBanner';
import { saidOf } from './subs/SubProgressBanner';
import {
  getTasks,
  getTask,
  createTask,
  bulkCreateTasks,
  updateTask,
  deleteTask,
  getSOVLines,
  getTaskAllocations,
  createAllocation,
  // Schedule-tab uses schedule_sections (project's WORK breakdown).
  // Distinct from SOV's `sections` table (project's COST breakdown).
  // Even when names happen to overlap (e.g. both have "Building A"),
  // they're separate rows in separate tables and don't cross-leak.
  getScheduleSections,
  createScheduleSection,
  deleteScheduleSection,
  reorderScheduleSections,
  addPredecessor,
  previewSchedule,
  updatePredecessor,
  removePredecessor,
  getProgressAlertsSummary,
  getDependencyAlerts,
  getSplitPreview
} from '../api';
import ConfirmDialog from './ConfirmDialog';
import LoadingState from './LoadingState';
import TaskSOVLinks from './TaskSOVLinks';
import useConfirm from '../hooks/useConfirm';
import { useTheme } from '../hooks/useTheme';
import { Icon, LoadingDialog, Menu, Popover, PopoverHeader, PopoverDivider, CheckMenuItem, RadioMenuItem, useHoverTip } from './ui';
import { rollUp } from '../lib/rollup';
import SectionCards, { SectionHead } from './SectionCards';
import SubtaskPlacement, { arrowOffers, handedArrows } from './SubtaskPlacement';
import SplitDown from './SplitDown';
import { SCHEDULE_SORT_OPTIONS, SCHEDULE_TASK_TYPES } from './scheduleSort';
import { useProjectToday, nowOn } from '../lib/projectToday';
import { needsDocs, todayYmd } from '../lib/subcontractors';
import { milestoneColor } from '../lib/milestoneColor';
import MilestoneColorPicker from './MilestoneColorPicker';
import '../App.css';

/* -------------------- toolbar helpers --------------------
 * Tiny ghost button (Filter / Sort) and square icon-only button used
 * in the redesigned Schedule action bar. Lifted to the module scope
 * so they don't re-render on every TasksTab tick.
 */
function GhostButton({ icon, label, active, onClick }) {
  const [hover, setHover] = useState(false);
  const bg = active ? 'var(--bg-surface)' : hover ? 'var(--bg-hover)' : 'transparent';
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        height: 28, padding: '0 10px',
        border: `1px solid ${active ? 'var(--border-default)' : 'transparent'}`,
        background: bg,
        color: active ? 'var(--text-1)' : 'var(--text-3)',
        fontSize: 13.5, fontWeight: 500, cursor: 'pointer', borderRadius: 6,
        boxShadow: active ? 'var(--shadow-xs)' : 'none',
        transition: 'background .12s, color .12s, border-color .12s',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon name={icon} size={13} />
      {label}
    </button>
  );
}

function IconUtilBtn({ name, label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 28, height: 28, border: 'none', borderRadius: 6,
        background: hover ? 'var(--bg-hover)' : 'transparent',
        color: hover ? 'var(--text-1)' : 'var(--text-3)',
        cursor: 'pointer', display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', padding: 0,
        transition: 'background .12s, color .12s',
      }}
    >
      <Icon name={name} size={14} />
    </button>
  );
}

/* Toolbar overflow (Duncan 2026-10-10). This panel shares the screen with the
 * Gantt, so it's usually narrower than the full action bar (~840px; the default
 * split gives 580px on a 1512px laptop). Instead of running off the edge, what
 * doesn't fit folds into one ⋮ menu at the end. Higher stays out longer: the
 * four icons fold first, then + Section, Sort, Filter, and + Task last. */
const TOOLBAR_KEEP = { task: 5, filter: 4, sort: 3, section: 2, template: 1, export: 1, import: 1, print: 1 };
const TOOLBAR_GAP = 6;
const TOOLBAR_MORE_W = 28; // the ⋮ button
const SEARCH_MIN_W = 180;  // the search box's own minWidth

// Helper function to format dates as MM-DD-YYYY
const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

// Task type color mapping matching Base44 design
const getTaskTypeStyle = (taskType) => {
  switch ((taskType || '').toLowerCase()) {
    case 'material':
    case 'procurement':
      return { backgroundColor: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D' };
    case 'labor':
    case 'work':
      return { backgroundColor: '#E3F2FD', color: '#1565C0', border: '1px solid #64B5F6' };
    case 'engineering':
      return { backgroundColor: '#E8F5E9', color: '#2E7D32', border: '1px solid #81C784' };
    case 'inspection':
      return { backgroundColor: '#F3E5F5', color: '#6A1B9A', border: '1px solid #BA68C8' };
    case 'documents':
      return { backgroundColor: '#EDE7F6', color: '#4527A0', border: '1px solid #9575CD' };
    case 'milestone':
      return { backgroundColor: '#FFF8E1', color: '#F57F17', border: '1px solid #FFD54F' };
    default:
      return { backgroundColor: '#F5F5F5', color: '#616161', border: '1px solid #BDBDBD' };
  }
};

/**
 * Schedule-redesign: map task_type → CSS custom property holding the
 * type color. Used for the 7px leading dot + Gantt bar fills. The
 * underlying tokens (engineering sage / procurement amber / labor blue
 * / inspection purple / milestone gold) live in styles/tokens.css and
 * flip between light + dark via .app.dark.
 */
const getTaskTypeColorVar = (taskType) => {
  switch ((taskType || '').toLowerCase()) {
    case 'material':
    case 'procurement':
      return 'var(--type-procurement)';
    case 'labor':
    case 'work':
      return 'var(--type-labor)';
    case 'engineering':
      return 'var(--type-engineering)';
    case 'inspection':
      return 'var(--type-inspection)';
    case 'documents':
      return 'var(--type-documents)';
    case 'milestone':
      return 'var(--type-milestone)';
    default:
      return 'var(--text-3)';
  }
};

// The list's columns: drag, code, task name, type, dates, % done. The name takes the room left over but never less
// than 160px: as a bare 1fr it was the column that gave way on a small screen (to nothing, its link arrows then sitting
// on the type). % Done fits its 50px bar and "100%". TASK_LIST_MIN_W is the list at its narrowest (the 12px gaps, the
// side padding and the 3px flag border too): ScheduleView gives it that much when the screen allows, and the list
// scrolls sideways when it can't.
const TASK_GRID_COLS = '24px 60px minmax(160px, 1fr) 90px 160px 88px';
export const TASK_LIST_MIN_W = 24 + 60 + 160 + 90 + 160 + 88 + 5 * 12 + 2 * 12 + 3;

/** Compact date "May 13" / "Jun 4". Used in the refined dates cell so
 *  the row stays under one line at the redesign's tighter spacing. */
const fmtCompactDate = (s) => {
  if (!s) return '';
  // 'YYYY-MM-DD' is a calendar day: read it as LOCAL midnight. new Date('2026-05-13') is UTC
  // midnight, so this column showed every date a day early in Central time.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** A task's duration as the schedule counts it (#52): working days on a Mon–Fri week,
 *  calendar days on a 7-day week, both ends included. From the dates, because duration_days
 *  was stored as calendar days before dynamic scheduling. */
const spanDays = (start, finish, workWeek) => {
  const day = (v) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || '')); return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000 : null; };
  const a = day(start);
  const b = day(finish);
  if (a == null || b == null || b < a) return 0;
  if (String(workWeek) === '7') return b - a + 1;
  let n = 0;
  for (let d = a; d <= b; d += 1) { const w = new Date(d * 86400000).getUTCDay(); if (w !== 0 && w !== 6) n += 1; }
  return n;
};

/** Stable hash → OKLCH hue for a section name. Mirror of the helper
 *  in GanttTab.js — kept in lockstep so the left section header dot
 *  matches the right panel's roll-up bar color. */
const sectionHue = (name) => {
  let h = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h % 360;
};
const sectionColorFor = (name) => `oklch(0.55 0.1 ${sectionHue(name)})`;


// Status badge style
const getStatusStyle = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'not started':
      return { backgroundColor: '#F5F5F5', color: '#757575', border: '1px solid #E0E0E0' };
    case 'in progress':
      return { backgroundColor: '#E3F2FD', color: '#1565C0', border: '1px solid #64B5F6' };
    case 'complete':
      return { backgroundColor: '#E8F5E9', color: '#2E7D32', border: '1px solid #81C784' };
    default:
      return { backgroundColor: '#F5F5F5', color: '#757575', border: '1px solid #E0E0E0' };
  }
};


const DEP_TYPES = [
  { value: 'FS', label: 'FS — Finish to Start', short: 'FS' },
  { value: 'SS', label: 'SS — Start to Start', short: 'SS' },
  { value: 'FF', label: 'FF — Finish to Finish', short: 'FF' },
  { value: 'SF', label: 'SF — Start to Finish', short: 'SF' },
];

// Predecessor section component matching Base44 design
// Supports two modes:
//   "api" mode (default): saves/removes via addPredecessor/removePredecessor API (for Task Properties)
//   "local" mode: manages predecessors in local state, returns them via onLocalChange (for Add Task modal)
// A link's lag counts work days, or calendar days that keep going over the weekend
// (Project's "ed", e.g. a concrete cure — #55).
const LagUnitSelect = ({ elapsed, onChange, style }) => (
  <select
    value={elapsed ? 'ed' : 'd'}
    onChange={(e) => onChange(e.target.value === 'ed')}
    style={style}
    title={'Work days skip weekends; calendar days keep counting through them (like MS Project\'s "ed")'}
  >
    <option value="d">work days</option>
    <option value="ed">calendar days</option>
  </select>
);

// A link's lag box that lets a lead be typed: a number box reports "" while "-" is typed, so
// it keeps its own text and passes on whole numbers ("-2" = a 2-day lead).
const LagNumberInput = ({ value, onChange, style }) => {
  const [text, setText] = useState(String(value || 0));
  useEffect(() => {
    if ((parseInt(text, 10) || 0) !== (value || 0)) setText(String(value || 0));
    // eslint-disable-next-line
  }, [value]);
  return (
    <input
      type="number"
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const n = parseInt(e.target.value, 10);
        onChange(Number.isNaN(n) ? 0 : n);
      }}
      onBlur={() => setText(String(value || 0))}
      style={style}
    />
  );
};

const PredecessorSection = ({ currentTask, tasks, editingTaskId, inputStyle, fieldGroup, addPredecessor, updatePredecessor, removePredecessor, fetchTasks, confirm, mode = 'api', localPredecessors = [], onLocalChange, excludeIds = [] }) => {
  const t = useTheme();
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPredTask, setSelectedPredTask] = useState(null);
  const [depType, setDepType] = useState('FS');
  const [lagDays, setLagDays] = useState(0);
  const [lagElapsed, setLagElapsed] = useState(false);

  const isLocal = mode === 'local';
  const currentPredecessors = isLocal
    ? localPredecessors
    : (currentTask?.predecessors || []).filter(p => p.predecessor_task_id != null);
  const alreadyLinkedIds = new Set(currentPredecessors.map(p => Number(p.predecessor_task_id)));

  // Available tasks: exclude self and already-linked -- and parents: arrows go between bottom rows (server/subtasks.js), so
  // a task with subtasks offers them instead -- and what the caller names (a new subtask's own task).
  const parentIds = new Set(tasks.filter((x) => x.parent_task_id != null).map((x) => Number(x.parent_task_id)));
  const skipIds = new Set(excludeIds.map(Number));
  const availableTasks = tasks
    .filter(t => t.id !== editingTaskId && !alreadyLinkedIds.has(Number(t.id)) && !parentIds.has(Number(t.id)) && !skipIds.has(Number(t.id)) && !t._addFor)
    .sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));

  // Filter by search text
  const filteredTasks = searchText.trim()
    ? availableTasks.filter(t => {
        const q = searchText.toLowerCase();
        return (t.task_code || '').toLowerCase().includes(q) ||
               (t.task_name || '').toLowerCase().includes(q) ||
               (t.building_area_phase || '').toLowerCase().includes(q);
      })
    : availableTasks;

  const handleSelectTask = async (task) => {
    if (isLocal) {
      // In local mode (Add Task), immediately add with defaults
      const newPred = {
        id: `local_${Date.now()}`,
        predecessor_task_id: task.id,
        dependency_type: 'FS',
        lag_days: 0,
      };
      onLocalChange([...localPredecessors, newPred]);
      setSearchText('');
      setShowDropdown(false);
    } else {
      // In API mode (Edit Task), immediately add with FS defaults (matching local mode behavior)
      setSearchText('');
      setShowDropdown(false);
      try {
        await addPredecessor(editingTaskId, {
          predecessor_task_id: task.id,
          dependency_type: 'FS',
          lag_days: 0,
        });
        fetchTasks({ preserveScroll: true });
      } catch (err) {
        console.error('Error adding predecessor:', err);
        alert('Failed to add predecessor: ' + err.message);
      }
    }
  };

  const handleAdd = async () => {
    if (!selectedPredTask) return;
    if (isLocal) {
      const newPred = {
        id: `local_${Date.now()}`,
        predecessor_task_id: selectedPredTask.id,
        dependency_type: depType,
        lag_days: lagDays,
        lag_elapsed: lagElapsed,
      };
      onLocalChange([...localPredecessors, newPred]);
      setSelectedPredTask(null);
      setDepType('FS');
      setLagDays(0);
      setLagElapsed(false);
    } else {
      try {
        await addPredecessor(editingTaskId, {
          predecessor_task_id: selectedPredTask.id,
          dependency_type: depType,
          lag_days: lagDays,
          lag_elapsed: lagElapsed,
        });
        setSelectedPredTask(null);
        setDepType('FS');
        setLagDays(0);
        setLagElapsed(false);
        fetchTasks({ preserveScroll: true });
      } catch (err) {
        console.error('Error adding predecessor:', err);
        alert('Failed to add predecessor: ' + err.message);
      }
    }
  };

  const handleRemove = async (predId) => {
    if (isLocal) {
      onLocalChange(localPredecessors.filter(p => p.id !== predId));
    } else {
      const ok = await confirm({ title: 'Remove Predecessor', message: 'Are you sure you want to remove this predecessor link?', confirmLabel: 'Remove', danger: true });
      if (!ok) return;
      try {
        await removePredecessor(editingTaskId, predId);
        fetchTasks({ preserveScroll: true });
      } catch (err) { console.error('Error removing predecessor:', err); }
    }
  };

  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '8px 12px', backgroundColor: t.bgAlt, borderRadius: '8px',
    marginBottom: '6px', border: `1px solid ${t.cardBorder}`,
  };

  return (
    <div style={fieldGroup}>
      {/* Existing predecessors */}
      {currentPredecessors.map(pred => {
        const predTask = tasks.find(t => Number(t.id) === Number(pred.predecessor_task_id));
        const dt = DEP_TYPES.find(d => d.value === pred.dependency_type) || DEP_TYPES[0];
        return (
          <div key={pred.id} style={{ ...rowStyle, flexWrap: 'wrap' }}>
            <div style={{
              flex: 1, fontSize: '14.5px', fontWeight: 500, color: t.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              minWidth: '120px',
            }}>
              <span style={{ color: '#3b82f6', fontWeight: 600, marginRight: '6px' }}>
                {predTask?.task_code || '?'}
              </span>
              {predTask ? predTask.task_name : `Task #${pred.predecessor_task_id}`}
              {predTask && (predTask.start_date || predTask.finish_date) && (
                <div style={{ fontSize: '12.5px', color: t.textMuted, fontWeight: 400, marginTop: '2px', whiteSpace: 'nowrap' }}>
                  {predTask.start_date ? `Start: ${fmtDate(predTask.start_date)}` : ''}
                  {predTask.start_date && predTask.finish_date ? '  —  ' : ''}
                  {predTask.finish_date ? `Finish: ${fmtDate(predTask.finish_date)}` : ''}
                </div>
              )}
            </div>
            <select value={pred.dependency_type || 'FS'}
              onChange={(e) => {
                if (isLocal) {
                  onLocalChange(localPredecessors.map(p => p.id === pred.id ? { ...p, dependency_type: e.target.value } : p));
                } else {
                  updatePredecessor(editingTaskId, pred.id, { dependency_type: e.target.value, lag_days: pred.lag_days || 0 })
                    .then(() => fetchTasks({ preserveScroll: true })).catch(err => console.error('Error updating predecessor:', err));
                }
              }}
              style={{
                padding: '4px 6px', fontSize: '13.5px', border: `1px solid ${t.cardBorder}`,
                borderRadius: '6px', backgroundColor: t.bg, color: t.text,
                width: '130px', flexShrink: 0, cursor: 'pointer',
              }}>
              {DEP_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <span style={{ fontSize: '12.5px', color: t.textSecondary, flexShrink: 0 }}>Lag</span>
            <LagNumberInput value={pred.lag_days || 0}
              onChange={(newLag) => {
                if (isLocal) {
                  onLocalChange(localPredecessors.map(p => p.id === pred.id ? { ...p, lag_days: newLag } : p));
                } else {
                  updatePredecessor(editingTaskId, pred.id, { dependency_type: pred.dependency_type || 'FS', lag_days: newLag })
                    .then(() => fetchTasks({ preserveScroll: true })).catch(err => console.error('Error updating predecessor:', err));
                }
              }}
              style={{
                width: '40px', padding: '4px 6px', fontSize: '13.5px', textAlign: 'center',
                border: `1px solid ${t.cardBorder}`, borderRadius: '6px', backgroundColor: t.bg,
              }} />
            <LagUnitSelect elapsed={pred.lag_elapsed === true}
              onChange={(elapsed) => {
                if (isLocal) {
                  onLocalChange(localPredecessors.map(p => p.id === pred.id ? { ...p, lag_elapsed: elapsed } : p));
                } else {
                  updatePredecessor(editingTaskId, pred.id, { dependency_type: pred.dependency_type || 'FS', lag_days: pred.lag_days || 0, lag_elapsed: elapsed })
                    .then(() => fetchTasks({ preserveScroll: true })).catch(err => console.error('Error updating predecessor:', err));
                }
              }}
              style={{
                padding: '4px 6px', fontSize: '13px', border: `1px solid ${t.cardBorder}`,
                borderRadius: '6px', backgroundColor: t.bg, color: t.text, flexShrink: 0, cursor: 'pointer',
              }} />
            <button type="button" onClick={() => handleRemove(pred.id)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                color: t.textMuted, fontSize: '17.5px', lineHeight: 1, flexShrink: 0,
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
              title="Remove predecessor"
            >&times;</button>
          </div>
        );
      })}

      {/* Selected task — show dep type + lag + confirm */}
      {selectedPredTask && (
        <div style={{ ...rowStyle, border: '1px solid #3b82f6', backgroundColor: '#eff6ff' }}>
          <div style={{
            flex: 1, fontSize: '14.5px', fontWeight: 500, color: t.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            <span style={{ color: '#3b82f6', fontWeight: 600, marginRight: '6px' }}>
              {selectedPredTask.task_code}
            </span>
            {selectedPredTask.task_name}
          </div>
          <select value={depType} onChange={(e) => setDepType(e.target.value)}
            style={{
              padding: '4px 6px', fontSize: '13.5px', border: '1px solid #93c5fd',
              borderRadius: '6px', backgroundColor: t.bg, color: t.text,
              width: '130px', flexShrink: 0,
            }}>
            {DEP_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <span style={{ fontSize: '12.5px', color: '#64748b', flexShrink: 0 }}>Lag</span>
          <input type="number" value={lagDays} onChange={(e) => setLagDays(parseInt(e.target.value) || 0)} min={0}
            style={{
              width: '40px', padding: '4px 6px', fontSize: '13.5px', textAlign: 'center',
              border: '1px solid #93c5fd', borderRadius: '6px', backgroundColor: '#fff',
            }} />
          <LagUnitSelect elapsed={lagElapsed} onChange={setLagElapsed}
            style={{
              padding: '4px 6px', fontSize: '13px', border: '1px solid #93c5fd',
              borderRadius: '6px', backgroundColor: t.bg, color: t.text, flexShrink: 0, cursor: 'pointer',
            }} />
          <button type="button" onClick={handleAdd}
            style={{
              background: '#3b82f6', border: 'none', color: '#fff', borderRadius: '6px',
              padding: '4px 10px', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
            }}>Add</button>
          <button type="button" onClick={() => setSelectedPredTask(null)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
              color: '#94a3b8', fontSize: '17.5px', lineHeight: 1, flexShrink: 0,
            }}>&times;</button>
        </div>
      )}

      {/* Search / Add dropdown — only show when no task is selected */}
      {!selectedPredTask && (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              placeholder="Search tasks to add as predecessor..."
              style={{
                ...inputStyle, fontSize: '14.5px', paddingRight: '32px',
              }}
            />
            <svg style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          {showDropdown && (
            <>
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                backgroundColor: t.bg, border: `1px solid ${t.cardBorder}`, borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: '200px', overflowY: 'auto',
                marginTop: '4px',
              }}>
                {filteredTasks.length === 0 ? (
                  <div style={{ padding: '12px 14px', fontSize: '13.5px', color: '#94a3b8', textAlign: 'center' }}>
                    No matching tasks
                  </div>
                ) : (
                  filteredTasks.map(t => (
                    <div key={t.id}
                      onClick={() => handleSelectTask(t)}
                      style={{
                        padding: '8px 14px', fontSize: '14.5px', cursor: 'pointer',
                        borderBottom: '1px solid var(--border-subtle)',
                        transition: 'background-color 0.1s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <span style={{ color: '#3b82f6', fontWeight: 600, marginRight: '8px' }}>{t.task_code}</span>
                      <span style={{ color: t.text }}>{t.task_name}</span>
                      {t.building_area_phase && (
                        <span style={{ color: t.textMuted, fontSize: '12.5px', marginLeft: '8px' }}>({t.building_area_phase})</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/** "Oct 24" ("Oct 24, 2025" in another year): when the sub put a technician on a task. */
const sinceDay = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || !Number.isFinite(d.getTime())) return null;
  return d.toLocaleDateString('en-US', d.getFullYear() === new Date().getFullYear()
    ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
};
/** The technicians' pop-up (Duncan 2026-10-28, as his mockup): who the sub put on the task, since when, and the sub. */
const techTipOf = (task, people) => (
  <>
    <div style={{ fontWeight: 600, marginBottom: 4 }}>On this task</div>
    {people.map((x) => (
      <div key={x.name}>{x.name}{sinceDay(x.since) && <span style={{ color: 'var(--text-3)' }}> · since {sinceDay(x.since)}</span>}</div>
    ))}
    <div style={{ color: 'var(--text-3)', marginTop: 6 }}>{task.responsible_party ? `${task.responsible_party}, on E3 Subs` : 'On E3 Subs'}</div>
  </>
);

const TasksTab = ({ projectId, refreshSummary, sharedSchedule, onTaskOpen, workWeek = '5', addSubtaskFor = null, onAddSubtaskOpened }) => {
  const t = useTheme();
  const { confirm, confirmDialogProps } = useConfirm();
  const { testDate } = useProjectToday(); // a test job's test date stands in for today (2026-10-20)
  // The technicians' instant pop-up (Duncan 2026-10-28; ui/HoverTip.js): one for the whole list.
  const [techTip, techTipOn] = useHoverTip();
  const [_localTasks, _setLocalTasks] = useState([]);
  const tasks = sharedSchedule ? sharedSchedule.tasks : _localTasks;
  const setTasks = sharedSchedule ? sharedSchedule.setTasks : _setLocalTasks;
  const [sovLines, setSovLines] = useState([]);
  const [taskAllocations, setTaskAllocations] = useState({});
  // Subtasks (2026-10-30, server/subtasks.js): a parent that still holds SOV dollars or a sub's billing its subtasks should
  // gets the split window (SplitDown.js) -- { ids, mode, after }. The Schedule opens with the ones waiting, one by one.
  const [lowWarn, setLowWarn] = useState(null);
  const queueShown = useRef(false);
  useEffect(() => {
    if (queueShown.current || !tasks.length) return;
    queueShown.current = true;
    const waiting = tasks.filter((x) => x.split_pending).map((x) => x.id);
    if (waiting.length) setLowWarn({ ids: waiting, mode: 'queue' });
  }, [tasks]);
  const [showModal, setShowModal] = useState(false);
  // Default to true so the very first render shows the loading spinner
  // instead of flashing the "No tasks yet" empty state before the
  // initial fetchTasks() resolves. Flips to false in fetchTasks's finally
  // block. In sharedSchedule mode, ScheduleView owns the loading flag and
  // we read it from sharedSchedule.loading at render time.
  const [loading, setLoading] = useState(true);
  // File-import progress dialog (mirrors the one in SOVTab). Shape:
  //   null                                            -> dialog hidden
  //   { total, status: 'importing' }                  -> bulk POST in flight
  //   { total, status: 'success', count }             -> done, auto-closes
  //   { total, status: 'error', error: str }          -> failed, user dismisses
  const [importProgress, setImportProgress] = useState(null);
  const [_localCollapsedSections, _setLocalCollapsedSections] = useState({});
  const collapsedSections = sharedSchedule ? sharedSchedule.collapsedSections : _localCollapsedSections;
  const setCollapsedSections = sharedSchedule ? sharedSchedule.setCollapsedSections : _setLocalCollapsedSections;
  const [formErrors, setFormErrors] = useState({});
  const [editingPercentCompleteId, setEditingPercentCompleteId] = useState(null);
  const [editingPercentCompleteValue, setEditingPercentCompleteValue] = useState('');
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  // (SOV linking is now handled inside the task Properties modal —
  // see TaskSOVLinks.js. The standalone SOVLinkModal that used to live
  // in this file was removed when the link icon on each row went away.)
  // Drag and drop state (tasks)
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverTask, setDragOverTask] = useState(null);
  const [dragSection, setDragSection] = useState(null);
  // Drag and drop state (sections)
  const [draggedSectionName, setDraggedSectionName] = useState(null);
  const [dragOverSectionName, setDragOverSectionName] = useState(null);
  const sectionDragRef = useRef(false); // synchronous flag for section drag
  // Persisted sections from backend
  const [_localSavedSections, _setLocalSavedSections] = useState([]);
  const savedSections = sharedSchedule ? sharedSchedule.savedSections : _localSavedSections;
  const setSavedSections = sharedSchedule ? sharedSchedule.setSavedSections : _setLocalSavedSections;
  const [sectionsLoaded, setSectionsLoaded] = useState(false);

  // Filter / Sort popover open state + anchor rect. The filter/sort
  // VALUES live on ScheduleView (so the Gantt panel sees the same
  // settings), but the POPOVER for managing them lives in this
  // component since the Filter/Sort buttons are in the Tasks panel's
  // action bar.
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  // 'right' when Filter / Sort are opened from the ⋮ menu: the popover then
  // opens under the menu button, over this panel, instead of over the Gantt.
  const [popoverAlign, setPopoverAlign] = useState('left');

  // Toolbar overflow: which buttons are folded into the ⋮ menu. A hidden copy of
  // the toolbar (measureRef) gives every button's natural width, so the fold can
  // be worked out before paint and again whenever the panel or a label resizes.
  const toolbarKeys = ['filter', 'sort', 'template', 'export', 'import', ...(sharedSchedule?.openPrintModal ? ['print'] : []), 'section', 'task'];
  const toolbarKeysSig = toolbarKeys.join(',');
  const [folded, setFolded] = useState([]);
  const headerRef = useRef(null);
  const titleRef = useRef(null);
  const measureRef = useRef(null);
  const moreRef = useRef(null);
  const importInputRef = useRef(null);
  const computeFold = useCallback(() => {
    const header = headerRef.current;
    const title = titleRef.current;
    const meas = measureRef.current;
    if (!header || !title || !meas) return;
    const keys = toolbarKeysSig.split(',');
    const w = {};
    meas.querySelectorAll('[data-tb]').forEach((el) => { w[el.dataset.tb] = el.offsetWidth; });
    const cs = getComputedStyle(header);
    const avail = header.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight)
      - title.offsetWidth - (parseFloat(cs.columnGap) || 0);
    const full = (w.search || 0) + keys.reduce((sum, k) => sum + TOOLBAR_GAP + (w[k] || 0), 0);
    let next = [];
    if (full > avail) {
      let room = avail - SEARCH_MIN_W - TOOLBAR_GAP - TOOLBAR_MORE_W;
      const shown = new Set();
      const byKeep = [...keys].sort((a, b) => (TOOLBAR_KEEP[b] - TOOLBAR_KEEP[a]) || (keys.indexOf(a) - keys.indexOf(b)));
      for (const k of byKeep) {
        if ((w[k] || 0) + TOOLBAR_GAP > room) break; // strict order: nothing jumps the queue
        shown.add(k);
        room -= (w[k] || 0) + TOOLBAR_GAP;
      }
      next = keys.filter((k) => !shown.has(k));
    }
    setFolded((prev) => (prev.join(',') === next.join(',') ? prev : next));
  }, [toolbarKeysSig]);
  // Panel resizes (dragging the split, the window). The fold only changes the
  // visible buttons, never an observed box, so this can't loop.
  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(computeFold);
    [headerRef.current, titleRef.current, measureRef.current].forEach((el) => el && ro.observe(el));
    return () => ro.disconnect();
  }, [computeFold]);

  // Add Section modal state
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [flaggedTaskIds, setFlaggedTaskIds] = useState(new Set());
  // Latest active Draft billing period's end date (YYYY-MM-DD string)
  // or null. Pulled from the progress-alerts-summary response. Used by
  // getMaxProgress so the client-side cap matches the server's rule:
  // tasks finishing within the active Draft AFP can be marked 100%
  // even when today is before their finish_date.
  const [latestDraftPeriodEnd, setLatestDraftPeriodEnd] = useState(null);
  const [_localDepAlertData, _setLocalDepAlertData] = useState({ alerts: [], flagged_task_ids: [], summary: {} });
  const depAlertData = sharedSchedule ? sharedSchedule.depAlerts : _localDepAlertData;
  const setDepAlertData = sharedSchedule ? () => {} : _setLocalDepAlertData;
  // Pending predecessors for the Add Task modal (saved after task is created)
  const [pendingPredecessors, setPendingPredecessors] = useState([]);
  const [arrowPicks, setArrowPicks] = useState({}); // the placement box's arrow checks (Wait too? / Move the arrow?), ticked unless unticked
  const [dragHint, setDragHint] = useState(null); // why a drop snapped back (subtasks stay in date order)
  useEffect(() => { if (!dragHint) return undefined; const tm = setTimeout(() => setDragHint(null), 6000); return () => clearTimeout(tm); }, [dragHint]);
  // Pending SOV links for the Add Task modal — collected locally, then
  // committed as allocations once the task has an id (same pattern as
  // pendingPredecessors, since an allocation needs a saved task_id).
  const [pendingSovLinks, setPendingSovLinks] = useState([]);
  const [newSectionName, setNewSectionName] = useState('');
  const [sectionNameError, setSectionNameError] = useState('');
  // Double-submit guards. `creatingTask` / `creatingSection` block the
  // primary modal submit buttons; `savingEdit` blocks the inline
  // properties-panel Save Changes button; `deletingTaskId` per-task so
  // one slow delete doesn't freeze unrelated row controls.
  const [creatingTask, setCreatingTask] = useState(false);
  const [creatingSection, setCreatingSection] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const [formData, setFormData] = useState({
    task_code: '',
    task_name: '',
    task_description: '',
    building_area_phase: '',
    task_type: 'Labor',
    start_date: '',
    finish_date: '',
    duration_days: 0,
    duration: '1',
    status: 'Not Started',
    percent_complete: 0,
    responsible_party: '',
    client_visible: true,
    milestone_flag: false,
    notes: ''
  });

  // Helper: get all existing section names (from tasks + persisted sections)
  const getExistingSections = () => {
    const sections = new Set();
    tasks.forEach(t => {
      const sec = (t.building_area_phase || '').trim();
      if (sec) sections.add(sec);
    });
    savedSections.forEach(s => sections.add(s.section_name));
    return Array.from(sections);
  };

  // Check if a section name already exists (case-insensitive), optionally excluding a specific original name
  const isSectionNameTaken = (name, excludeOriginal = null) => {
    const normalized = (name || '').trim().toLowerCase();
    if (!normalized) return false;
    return getExistingSections().some(sec => {
      if (excludeOriginal && sec.toLowerCase() === excludeOriginal.toLowerCase()) return false;
      return sec.toLowerCase() === normalized;
    });
  };

  // Fetch sections from backend
  const fetchSections = async () => {
    try {
      const data = await getScheduleSections(projectId);
      console.log('[fetchSections] Loaded:', data.map(s => `${s.section_name}(${s.sort_order})`));
      setSavedSections(data);
      setSectionsLoaded(true);
    } catch (error) {
      console.error('Error fetching sections:', error);
      setSectionsLoaded(true);
    }
  };

  // Refresh both alert sources from the server. Pulled out of the
  // initial useEffect so handlePercentCompleteChange (and any future
  // task-update handler) can re-run it after a save — without this,
  // amber borders on rows that were "behind schedule" linger after
  // the PM marks them caught-up because the client-cached
  // flaggedTaskIds / depAlertData stay frozen at mount-time.
  //
  // Debounced (800 ms) so rapid edits — slider drags, bulk imports,
  // PMs marking N tasks done back-to-back — collapse into a single
  // server round-trip instead of N. Without this, every save was
  // firing 2 extra GETs and rapidly chewing through the 500/min
  // rate limit. Pass { immediate: true } to fire right now (used
  // on mount).
  const alertRefreshTimerRef = useRef(null);
  const refreshTaskAlerts = useCallback(({ immediate = false } = {}) => {
    const doFetch = () => {
      alertRefreshTimerRef.current = null;
      getProgressAlertsSummary(projectId).then(data => {
        const ids = new Set();
        (data.summaries || []).forEach(s => (s.flagged_task_ids || []).forEach(id => ids.add(id)));
        (data.overdue_task_ids || []).forEach(id => ids.add(id));
        setFlaggedTaskIds(ids);
        setLatestDraftPeriodEnd(data.latest_draft_period_end || null);
      }).catch(console.error);
      if (!sharedSchedule) {
        getDependencyAlerts(projectId).then(data => {
          setDepAlertData(data);
        }).catch(console.error);
      }
    };
    if (alertRefreshTimerRef.current) clearTimeout(alertRefreshTimerRef.current);
    if (immediate) doFetch();
    else alertRefreshTimerRef.current = setTimeout(doFetch, 800);
  }, [projectId, sharedSchedule]);

  // A new test date moves today, so the overdue flags move with it (test jobs, 2026-10-20).
  const testDateSeen = useRef(testDate);
  useEffect(() => {
    if (testDateSeen.current === testDate) return;
    testDateSeen.current = testDate;
    refreshTaskAlerts({ immediate: true });
  }, [testDate, refreshTaskAlerts]);

  // Cancel any pending refresh on unmount / project change so we don't
  // setState on an unmounted component.
  useEffect(() => () => {
    if (alertRefreshTimerRef.current) {
      clearTimeout(alertRefreshTimerRef.current);
      alertRefreshTimerRef.current = null;
    }
  }, [projectId]);

  // Optimistic local flag-clear: if the user just marked a task ≥100%
  // (Complete), strip it from flaggedTaskIds immediately so the amber
  // border disappears with no waiting on the server. Server will
  // confirm via refreshTaskAlerts after the debounce. We don't try to
  // optimistically re-flag in the other direction — server is the
  // authority on partial-progress overdue logic.
  const clearFlagLocally = useCallback((taskId) => {
    setFlaggedTaskIds(prev => {
      if (!prev.has(taskId)) return prev;
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
  }, []);

  // Fetch tasks-only data (SOV lines, progress alerts, dep alerts) when the
  // project changes. When sharedSchedule owns the task list, we skip the
  // tasks/sections/depAlerts fetches; ScheduleView handles those.
  useEffect(() => {
    if (!sharedSchedule) {
      fetchTasks();
      fetchSections();
    }
    fetchSOVLines();
    refreshTaskAlerts({ immediate: true });
  }, [projectId, refreshTaskAlerts]);

  // Allocations are fetched on-demand (when the SOV link panel opens for a
  // specific task), not in bulk on mount — bulk-loading N allocations in
  // parallel was eating the request budget for large projects.

  const fetchTasks = async ({ preserveScroll = false } = {}) => {
    if (sharedSchedule) {
      await sharedSchedule.refreshTasks();
      // Refresh allocations alongside since the SOV Links column needs them.
      // Fetch the current list directly — sharedSchedule.tasks is a closure
      // snapshot from render time and won't include a task just created this
      // tick, so iterating it would skip the new task's allocations (leaving
      // its SOV-link badge blank even though the links were saved).
      try {
        const fresh = await getTasks(projectId);
        for (const task of fresh) {
          try {
            const allocs = await getTaskAllocations(task.id);
            setTaskAllocations(prev => ({ ...prev, [task.id]: allocs }));
          } catch (e) { /* ignore */ }
        }
      } catch (e) { /* ignore */ }
      return;
    }
    // Save scroll positions before re-fetching so we can restore them
    const savedWindowScroll = preserveScroll ? window.scrollY : 0;
    const scrollableEls = preserveScroll ? Array.from(document.querySelectorAll('[style*="overflow"]')).filter(el => el.scrollTop > 0).map(el => ({ el, top: el.scrollTop })) : [];

    try {
      // Only show loading spinner on initial load, not on preserveScroll refreshes
      if (!preserveScroll) setLoading(true);

      // A task's code is its place in the schedule, saved by the server (server/taskCodes.js).
      const data = await getTasks(projectId);

      setTasks(data);
      // Fetch allocations for all tasks to populate SOV Links column
      const allocMap = {};
      for (const task of data) {
        try {
          const allocs = await getTaskAllocations(task.id);
          allocMap[task.id] = allocs;
        } catch (e) { /* ignore */ }
      }
      setTaskAllocations(prev => ({ ...prev, ...allocMap }));

      // Restore scroll positions after React re-renders with new data
      if (preserveScroll) {
        // Double rAF ensures we restore AFTER React has committed the DOM update
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, savedWindowScroll);
            scrollableEls.forEach(({ el, top }) => { if (document.contains(el)) el.scrollTop = top; });
          });
        });
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      if (!preserveScroll) setLoading(false);
    }
  };

  const fetchSOVLines = async () => {
    try {
      const data = await getSOVLines(projectId);
      setSovLines(data);
    } catch (error) {
      console.error('Error fetching SOV lines:', error);
    }
  };

  const fetchTaskAllocations = async (taskId) => {
    try {
      const data = await getTaskAllocations(taskId);
      setTaskAllocations(prev => ({ ...prev, [taskId]: data }));
    } catch (error) {
      console.error('Error fetching task allocations:', error);
    }
  };

  // Auto-calculate duration_days when dates change
  useEffect(() => {
    if (formData.start_date && formData.finish_date) {
      const start = new Date(formData.start_date);
      const finish = new Date(formData.finish_date);
      const duration = Math.ceil((finish - start) / (1000 * 60 * 60 * 24)) + 1;
      setFormData(prev => ({ ...prev, duration_days: Math.max(0, duration) }));
    }
  }, [formData.start_date, formData.finish_date]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newData = { [name]: type === 'checkbox' ? checked : value };
    // When start date changes and finish date is before the new start, clear finish date
    if (name === 'start_date' && value && formData.finish_date && formData.finish_date < value) {
      newData.finish_date = '';
    }
    setFormData(prev => ({ ...prev, ...newData }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // #51: where a new task lands. Its predecessors and duration decide (the server's schedule
  // engine — the same one that saves it), or a start typed here; the finish is calculated.
  const [addPreview, setAddPreview] = useState(null);
  // New task's Start and Finish on one calendar, as in the task window (Duncan 2026-10-25: DateRangePicker).
  const [addPicker, setAddPicker] = useState(null); // 'start' | 'finish' | null
  const addStartRef = useRef(null);
  const addFinishRef = useRef(null);
  const pendingKey = pendingPredecessors.map((p) => `${p.predecessor_task_id}:${p.dependency_type}:${p.lag_days}:${p.lag_elapsed ? 'ed' : 'd'}`).join(',');
  useEffect(() => {
    if (!showModal || !projectId) return undefined;
    let live = true;
    const timer = setTimeout(async () => {
      const days = parseInt(formData.duration, 10);
      const typed = formData.start_date || null;
      const pin = !!typed;
      try {
        const p = await previewSchedule(projectId, {
          task: {
            start_date: typed,
            parent_task_id: formData.parent_task_id || null, // a new subtask starts on its task's start (scheduleCascade.js)
            milestone_flag: formData.milestone_flag ? 1 : 0,
            duration_days: formData.milestone_flag ? 1 : (days > 0 ? days : 1),
            constraint_type: pin ? 'SNET' : null,
            constraint_date: pin ? typed : null,
          },
          predecessors: pendingPredecessors.map((p) => ({ predecessor_task_id: p.predecessor_task_id, dependency_type: p.dependency_type, lag_days: p.lag_days, lag_elapsed: p.lag_elapsed === true })),
        });
        if (live) {
          setAddPreview(p);
          // A start now placed -- typed, picked on the calendar or from a predecessor: the "pick one" error is answered.
          if (p && p.start_date) setFormErrors((prev) => (prev.start_date ? { ...prev, start_date: '' } : prev));
        }
      } catch (err) {
        if (live) setAddPreview(null);
      }
    }, 250);
    return () => { live = false; clearTimeout(timer); };
    // eslint-disable-next-line
  }, [showModal, projectId, formData.start_date, formData.duration, formData.milestone_flag, formData.parent_task_id, pendingKey]);

  // The task a new one is a subtask of ("Subtask of"): one of its section's top-level tasks, never a milestone.
  const addParent = formData.parent_task_id
    ? tasks.find((x) => x.id === Number(formData.parent_task_id) && (x.building_area_phase || '') === (formData.building_area_phase || '')
      && x.parent_task_id == null && !Number(x.milestone_flag)) || null
    : null;

  const handleAddTask = async () => {
    if (creatingTask) return;
    const errors = {};
    if (!formData.task_name || !formData.task_name.trim()) errors.task_name = 'Task name is required';
    if (!formData.building_area_phase) errors.building_area_phase = 'Please select a section';
    // The schedule places the task: it needs a duration, and a predecessor or a start date.
    if (!formData.milestone_flag && !(parseInt(formData.duration, 10) > 0)) errors.duration = 'Enter a duration in days';
    if (!addPreview || !addPreview.start_date) errors.start_date = 'Pick a predecessor below, or a start date';

    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    try {
      setCreatingTask(true);
      // Duration in the project's days; dates from the schedule preview (#51).
      const duration = formData.milestone_flag ? 1 : parseInt(formData.duration, 10);
      const typedStart = formData.start_date || null;
      const pinStart = !!typedStart;

      // Cap progress the same way the edit path does: a task can't be more
      // complete than its schedule has elapsed. POST /tasks doesn't enforce this
      // (PUT does), and the Status dropdown can request 100/50 — so clamp here to
      // getMaxProgress and keep status consistent, or a future task marked
      // "Complete" would persist an inflated earned_amount on any linked SOV line.
      const rawPct = parseFloat(formData.percent_complete) || 0;
      const maxPct = getMaxProgress({ start_date: addPreview.start_date, finish_date: addPreview.finish_date });
      const cappedPct = Math.max(0, Math.min(maxPct, rawPct));
      const cappedStatus = cappedPct >= 100 ? 'Complete' : cappedPct > 0 ? 'In Progress' : 'Not Started';

      const taskData = {
        project_id: projectId, // its code is its place in the schedule, given by the server (server/taskCodes.js)
        task_name: formData.task_name,
        task_description: formData.task_description || '',
        building_area_phase: formData.building_area_phase,
        task_type: formData.task_type || 'Labor',
        start_date: addPreview.start_date,
        finish_date: addPreview.finish_date,
        duration_days: duration,
        // A typed start is a pin ("start no earlier than"), as in Microsoft Project.
        constraint_type: pinStart ? 'SNET' : null,
        constraint_date: pinStart ? typedStart : null,
        status: cappedStatus,
        percent_complete: cappedPct,
        responsible_party: formData.responsible_party || '',
        subcontractor_id: formData.subcontractor_id || null,
        client_visible: formData.client_visible ? 1 : 0,
        milestone_flag: formData.milestone_flag ? 1 : 0,
        milestone_color: formData.milestone_flag ? formData.milestone_color || null : null,
        notes: formData.notes || '',
        // A subtask of one of its section's tasks (server/subtasks.js checks it again)
        parent_task_id: addParent ? addParent.id : null,
      };
      // A subtask with no sub picked starts with its task's (the server's default: the one its sisters share).
      if (addParent && !taskData.subcontractor_id && !String(taskData.responsible_party || '').trim()) delete taskData.subcontractor_id;
      // Its task's first subtask takes the task's arrows (subtasks.js moveArrowsDown): one picked here as well isn't added twice.
      const handed = addParent && !tasks.some((x) => Number(x.parent_task_id) === addParent.id)
        ? new Set((addParent.predecessors || []).map((x) => Number(x.predecessor_task_id))) : new Set();

      // A section that's only on this task so far (a banner's "Closeout") is made with it, as + Add New Section makes one.
      if (!getExistingSections().includes(formData.building_area_phase)) {
        await createScheduleSection({ project_id: projectId, section_name: formData.building_area_phase }).catch(() => {});
        fetchSections();
      }
      const newTask = await createTask(taskData);

      // The placement box's arrow checks (Duncan 2026-10-30, the arrows decision), each ticked unless unticked: wait for what
      // its first sisters wait for, and take over the arrow a task outside had on the old last subtask.
      if (newTask?.id && addParent) {
        const me = { id: newTask.id, task_name: taskData.task_name, start_date: taskData.start_date, finish_date: taskData.finish_date };
        const offers = arrowOffers({ parent: addParent, siblings: tasks.filter((x) => Number(x.parent_task_id) === addParent.id), me, myPreds: pendingPredecessors, allTasks: tasks, isNew: true });
        for (const o of offers.filter((x) => arrowPicks[x.key] !== false)) {
          try {
            if (o.kind === 'wait') await addPredecessor(newTask.id, { predecessor_task_id: o.predId, dependency_type: 'FS', lag_days: 0 });
            else {
              await removePredecessor(o.taskId, o.link.id);
              await addPredecessor(o.taskId, { predecessor_task_id: newTask.id, dependency_type: o.link.dependency_type || 'FS', lag_days: o.link.lag_days || 0, lag_elapsed: o.link.lag_elapsed === true });
            }
          } catch (arrowErr) { console.error('Arrow check failed:', arrowErr); }
        }
      }
      setArrowPicks({});

      // Save any pending predecessors
      let failedPreds = 0;
      if (pendingPredecessors.length > 0 && newTask?.id) {
        // The driving link first: the new task then already sits on its final dates, so the
        // other links don't move it (and log the moves) on the way.
        const driverId = addPreview && addPreview.driver && typeof addPreview.driver === 'object' ? Number(addPreview.driver.id) : null;
        const ordered = [...pendingPredecessors].filter((x) => !handed.has(Number(x.predecessor_task_id)))
          .sort((a, b) => (Number(b.predecessor_task_id) === driverId) - (Number(a.predecessor_task_id) === driverId));
        for (const pred of ordered) {
          try {
            await addPredecessor(newTask.id, {
              predecessor_task_id: pred.predecessor_task_id,
              dependency_type: pred.dependency_type,
              lag_days: pred.lag_days,
              lag_elapsed: pred.lag_elapsed === true,
            });
          } catch (predErr) {
            console.error('Error adding predecessor:', predErr);
            failedPreds += 1;
          }
        }
      }

      // Commit any pending SOV links — an allocation needs the saved task_id,
      // so (like predecessors) they were collected locally and are created now.
      let failedLinks = 0;
      let linkWhy = ''; // the server's reason (a billed line can't go backwards, billingFloor.js)
      if (pendingSovLinks.length > 0 && newTask?.id) {
        for (const link of pendingSovLinks) {
          try {
            await createAllocation({
              project_id: projectId,
              task_id: newTask.id,
              sov_line_id: link.sov_line_id,
              allocation_method: link.allocation_method,
              allocation_percent: link.allocation_percent,
              allocation_amount: link.allocation_amount,
            });
          } catch (allocErr) {
            console.error('Error adding SOV link:', allocErr);
            failedLinks += 1;
            if (!linkWhy && allocErr?.message) linkWhy = allocErr.message;
          }
        }
      }

      // The task itself was created; if any linked extras failed to attach, say
      // so rather than reporting a clean success — otherwise the user thinks
      // their SOV links / predecessors saved when they didn't.
      if (failedPreds || failedLinks) {
        const parts = [];
        if (failedLinks) parts.push(`${failedLinks} SOV link${failedLinks === 1 ? '' : 's'}`);
        if (failedPreds) parts.push(`${failedPreds} predecessor${failedPreds === 1 ? '' : 's'}`);
        alert(`Task "${taskData.task_name}" was created, but ${parts.join(' and ')} couldn't be saved — add ${parts.length > 1 || (failedLinks > 1 || failedPreds > 1) ? 'them' : 'it'} from the task's Properties.${linkWhy ? `\n\n${linkWhy}` : ''}`);
      }

      // A subtask under a task that holds SOV dollars or a sub's billing: the split window, on top of this one, which closes
      // after it (Duncan 10-30: "as soon as basically the infraction happens ... in front of the current windows").
      if (addParent) {
        const held = await getSplitPreview(addParent.id).catch(() => null);
        if (held && (held.ours.length || held.subs.length)) {
          fetchTasks();
          setLowWarn({ ids: [addParent.id], mode: 'moment', after: () => {
            setShowModal(false); setFormErrors({}); setPendingPredecessors([]); setPendingSovLinks([]);
            setFormData({ task_code: '', task_name: '', task_description: '', building_area_phase: '', task_type: 'Labor', start_date: '', finish_date: '',
              duration_days: 0, duration: '1', status: 'Not Started', percent_complete: 0, responsible_party: '', client_visible: true, milestone_flag: false, notes: '' });
          } });
          return;
        }
      }

      setShowModal(false);
      setFormErrors({});
      setPendingPredecessors([]);
      setPendingSovLinks([]);
      setFormData({
        task_code: '', task_name: '', task_description: '', building_area_phase: '',
        task_type: 'Labor', start_date: '', finish_date: '', duration_days: 0, duration: '1',
        status: 'Not Started', percent_complete: 0,
        responsible_party: '', client_visible: true, milestone_flag: false, notes: ''
      });
      fetchTasks();
      refreshSummary();
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task: ' + error.message);
    } finally {
      setCreatingTask(false);
    }
  };

  const handleUpdateTask = async (taskId, updates, { preserveScroll = false } = {}) => {
    try {
      await updateTask(taskId, updates);
      fetchTasks({ preserveScroll });
      refreshSummary();
      refreshTaskAlerts();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (deletingTaskId) return;
    const ok = await confirm({ title: 'Delete Task', message: 'Are you sure you want to delete this task? This action cannot be undone.', confirmLabel: 'Delete', danger: true });
    if (ok) {
      try {
        setDeletingTaskId(taskId);
        await deleteTask(taskId);
        setEditingTaskId(null);
        // The server renumbers the tasks after it (server/taskCodes.js); show them as they are now.
        fetchTasks({ preserveScroll: true });
      } catch (error) {
        console.error('Error deleting task:', error);
      } finally {
        setDeletingTaskId(null);
      }
    }
  };

  // Calculate max allowed progress for a task based on schedule proration.
  // Reference date = the active Draft AFP's period_end_date if one
  // exists on the project, otherwise today. Matches the server-side
  // proration rule in tasks.js PUT /:id so the slider, the validation
  // alert, AND the eventual save all agree on the cap. Lets PMs mark a
  // task finishing May 28 as 100% on May 25 when the billing period
  // ends May 31.
  const getMaxProgress = (task) => {
    if (!task || !task.start_date || !task.finish_date) return 100;
    const today = nowOn(testDate);
    today.setHours(23, 59, 59, 999);
    const draftEnd = latestDraftPeriodEnd
      ? new Date(latestDraftPeriodEnd + 'T23:59:59')
      : null;
    const refDate = draftEnd || today;
    const taskStart = new Date(task.start_date + 'T00:00:00');
    const taskEnd = new Date(task.finish_date + 'T23:59:59');
    const dayMs = 1000 * 60 * 60 * 24;
    const totalDuration = Math.max(1, Math.round((taskEnd - taskStart) / dayMs) + 1);
    if (refDate < taskStart) return 0;
    if (refDate >= taskEnd) return 100;
    const daysElapsed = Math.max(1, Math.round((refDate - taskStart) / dayMs) + 1);
    return Math.min(100, Math.ceil((daysElapsed / totalDuration) * 100));
  };

  const handlePercentCompleteChange = async (taskId, newValue) => {
    const task = tasks.find(tk => tk.id === taskId);
    // Snapshot prior values for rollback if the save fails — otherwise
    // a 429 / 400 / network blip wipes the optimistic % to whatever's
    // in the DB (usually 0) with no explanation to the user.
    const prevPct = parseFloat(task?.percent_complete) || 0;
    const prevStatus = task?.status || 'Not Started';
    const maxPct = getMaxProgress(task);
    // Hard clamp: never allow more than the prorated max
    const percentComplete = Math.min(maxPct, Math.max(0, parseInt(newValue) || 0));

    // Show warning if user tried to exceed the cap
    if (parseInt(newValue) > maxPct && maxPct < 100) {
      if (maxPct === 0) {
        confirm({
          title: 'Cannot Set Progress',
          message: `This task is scheduled entirely in the future (starts ${task.start_date}). Progress must remain at 0% because work cannot be billed for dates that haven't occurred yet.`,
          confirmLabel: 'OK',
          danger: false,
        });
      } else {
        // Phrase the cap relative to whichever reference is in effect.
        // matches getMaxProgress + the server-side check.
        const refLabel = latestDraftPeriodEnd
          ? `the active billing period ends ${latestDraftPeriodEnd}`
          : `today is ${testDate || new Date().toISOString().split('T')[0]}`;
        confirm({
          title: 'Progress Capped',
          message: `This task is scheduled ${task.start_date} – ${task.finish_date}. By ${refLabel}, only ${maxPct}% of the schedule will have elapsed. Maximum allowed progress: ${maxPct}%. To enter more, adjust the task finish date or extend the billing period.`,
          confirmLabel: 'OK',
          danger: false,
        });
      }
      // Still save the clamped value (don't return early) so the UI stays consistent
      if (percentComplete === (parseFloat(task?.percent_complete) || 0)) return;
    }

    // Determine new status based on percentage
    let newStatus = 'Not Started';
    if (percentComplete >= 100) newStatus = 'Complete';
    else if (percentComplete > 0) newStatus = 'In Progress';
    // Optimistic update: patch local state immediately so no re-fetch/re-render flicker
    setTasks(prev => prev.map(t =>
      t.id === taskId ? { ...t, percent_complete: percentComplete, status: newStatus } : t
    ));
    // If the task just hit 100%, clear its amber border locally too — server
    // will reconfirm on the debounced refresh, but the UI shouldn't wait.
    if (percentComplete >= 100) clearFlagLocally(taskId);
    // Save to server in the background — no fetchTasks, no await.
    // After a successful save, debounced-refresh the progress/overdue/dep
    // alerts so flaggedTaskIds eventually reconverges with the server.
    updateTask(taskId, { percent_complete: percentComplete, status: newStatus })
      .then(() => {
        refreshSummary();
        refreshTaskAlerts();
        // A subtask's % rolls up into its task on the server (subtasks.js rollUpParents): bring that task's new % and
        // status in too, so its row and Gantt bar follow without a reload (the subtasks video, 2026-11-02).
        if (task?.parent_task_id) {
          getTask(task.parent_task_id)
            .then((p) => setTasks(prev => prev.map(t =>
              t.id === p.id ? { ...t, percent_complete: p.percent_complete, status: p.status } : t
            )))
            .catch((err) => console.error('Error refreshing the parent task:', err));
        }
      })
      .catch((error) => {
        console.error('Error updating task:', error);
        // Local rollback only — no extra fetchTasks request (which would
        // itself be rate-limited). Restore the row to its prior state
        // and tell the user the save didn't land.
        setTasks(prev => prev.map(t =>
          t.id === taskId ? { ...t, percent_complete: prevPct, status: prevStatus } : t
        ));
        const msg = error?.message || String(error);
        alert(`Failed to save progress: ${msg}`);
      });
  };

  // ============ DRAG AND DROP ============
  const handleDragStart = (e, task, section) => {
    if (sectionDragRef.current) { e.preventDefault(); return; } // Don't start task drag during section drag
    setDraggedTask(task);
    setDragSection(section);
    e.dataTransfer.setData('text/plain', 'task:' + task.id);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedTask(null);
    setDragOverTask(null);
    setDragSection(null);
  };

  const handleDragOver = (e, task) => {
    if (sectionDragRef.current) return; // Section drag in progress — let it bubble
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (task.id !== dragOverTask) {
      setDragOverTask(task.id);
    }
  };

  const handleDrop = async (e, targetTask, section) => {
    if (sectionDragRef.current) return; // Section drag in progress — let it bubble
    e.preventDefault();
    if (!draggedTask || draggedTask.id === targetTask.id) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }

    // Subtasks (Duncan 2026-10-30, the six): a drag only reorders. A subtask stays in date order under its task, so dropped
    // anywhere it snaps back, and the hint says how to move it; a task dropped among another's subtasks lands by that task.
    if (draggedTask.parent_task_id != null) {
      const sister = targetTask.parent_task_id != null && Number(targetTask.parent_task_id) === Number(draggedTask.parent_task_id);
      const parentName = (tasks.find((t) => t.id === Number(draggedTask.parent_task_id)) || {}).task_name || 'its task';
      setDraggedTask(null);
      setDragOverTask(null);
      setDragHint(sister
        ? `Subtasks stay in date order under ${parentName}. To move "${draggedTask.task_name}", change its dates.`
        : `To move "${draggedTask.task_name}" under another task, use "Subtask of" in its window. Dragging only reorders.`);
      return;
    }

    // Only allow reorder within same section
    if (dragSection !== section) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }
    if (targetTask._addFor || targetTask.parent_task_id != null) {
      targetTask = tasks.find((t) => t.id === Number(targetTask._addFor || targetTask.parent_task_id)) || targetTask;
      if (targetTask.id === draggedTask.id) { setDraggedTask(null); setDragOverTask(null); return; }
    }

    // Whole-list reorder, but only when both drag + drop are in the
    // same section (the guard above already returned early otherwise).
    // The grouping UI buckets by building_area_phase regardless of
    // sort_order, so dragging cross-section would leave the task
    // visually in its original section anyway -- confusing. Within
    // section we still rewrite GLOBAL sort_order so the server's
    // positional task_code stays sequential across the whole list.
    // The server's order: sort_order, an unplaced task last, then id -- the top-level rows; a parent's subtasks ride along with
    // it and keep their own (date) order (server/taskCodes.js).
    const placeOf = (t) => (t.sort_order == null ? Infinity : Number(t.sort_order));
    const ordered = [...tasks].filter((t) => t.parent_task_id == null).sort(
      (a, b) => (placeOf(a) - placeOf(b)) || (Number(a.id) - Number(b.id))
    );
    const dragIdx = ordered.findIndex(t => t.id === draggedTask.id);
    const dropIdx = ordered.findIndex(t => t.id === targetTask.id);
    if (dragIdx === -1 || dropIdx === -1) return;

    const [moved] = ordered.splice(dragIdx, 1);
    ordered.splice(dropIdx, 0, moved);

    // Reassign sort_order to match the new positions. Use index*10 so
    // there's room to slot a new task in between later without renum-
    // bering everything (cheap optimization, harmless if unused).
    const updates = ordered.map((task, idx) => ({
      id: task.id,
      newSortOrder: (idx + 1) * 10,
    }));

    // Optimistic UI: rewrite sort_order locally AND patch task_code so
    // the row labels update immediately (server will return the same
    // positional codes on next fetch). We stamp `TSK-NNNN` here using
    // the same rule the server uses (idx + 1, zero-padded to 4).
    const codeOf = new Map(ordered.map((t, idx) => [t.id, `TSK-${String(idx + 1).padStart(4, '0')}`]));
    const placeAt = new Map(ordered.map((t, idx) => [t.id, updates[idx].newSortOrder]));
    setTasks((prev) => prev.map((t) => {
      if (t.parent_task_id == null) return placeAt.has(t.id) ? { ...t, sort_order: placeAt.get(t.id), task_code: codeOf.get(t.id) } : t;
      const pc = codeOf.get(Number(t.parent_task_id));
      const k = String(t.task_code || '').split('.')[1]; // a subtask follows its task's code
      return pc && k ? { ...t, task_code: `${pc}.${k}` } : t;
    }));

    setDraggedTask(null);
    setDragOverTask(null);

    // Persist sort_order to the backend. task_code is recomputed at
    // read time on the server (see server/routes/tasks.js), so we don't
    // bother writing it -- the next GET regenerates it.
    for (const upd of updates) {
      try {
        await updateTask(upd.id, { sort_order: upd.newSortOrder });
      } catch (err) {
        console.error('Error updating task sort_order:', err);
      }
    }
  };

  // ============ SECTION DRAG AND DROP ============
  const handleSectionDragStart = (e, sectionName) => {
    e.stopPropagation();
    sectionDragRef.current = true;
    setDraggedSectionName(sectionName);
    e.dataTransfer.setData('text/plain', 'section:' + sectionName);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleSectionDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    sectionDragRef.current = false;
    setDraggedSectionName(null);
    setDragOverSectionName(null);
  };

  const handleSectionDragOver = (e, sectionName) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (sectionName !== dragOverSectionName) {
      setDragOverSectionName(sectionName);
    }
  };

  const sectionsRef = useRef([]);

  const handleSectionDrop = async (e, targetSection) => {
    e.preventDefault();
    e.stopPropagation();

    const dragged = draggedSectionName;
    sectionDragRef.current = false;
    setDraggedSectionName(null);
    setDragOverSectionName(null);

    if (!dragged || dragged === targetSection) return;

    const currentOrder = [...sectionsRef.current];
    const dragIdx = currentOrder.indexOf(dragged);
    const dropIdx = currentOrder.indexOf(targetSection);
    if (dragIdx < 0 || dropIdx < 0) return;

    // Reorder
    currentOrder.splice(dragIdx, 1);
    currentOrder.splice(dropIdx, 0, dragged);

    // Optimistic UI — update savedSections immediately so render reflects new order
    const optimistic = currentOrder.map((name, i) => {
      const existing = savedSections.find(s => s.section_name === name);
      return existing ? { ...existing, sort_order: i } : { section_name: name, sort_order: i, project_id: projectId };
    });
    setSavedSections(optimistic);

    // Persist to backend
    try {
      console.log('[SectionDrop] Saving order:', currentOrder);
      const updated = await reorderScheduleSections(projectId, currentOrder);
      console.log('[SectionDrop] Server returned:', updated.map(s => `${s.section_name}(${s.sort_order})`));
      setSavedSections(updated);
    } catch (err) {
      console.error('[SectionDrop] Error reordering sections:', err);
    }
  };

  // (SOV linking handlers used to live here. They were only called by
  // the standalone SOVLinkModal — both the modal and its triggers were
  // removed when SOV linking moved into TaskSOVLinks inside the task
  // Properties dialog.)

  // ============ GROUP & SORT TASKS ============
  // When sharedSchedule is provided, use the parent's precomputed groups/sections
  // so both Tasks and Gantt render the same rows in the same order.
  const _localGroupedTasks = tasks.reduce((acc, task) => {
    const section = task.building_area_phase || 'Unsectioned';
    if (!acc[section]) acc[section] = [];
    acc[section].push(task);
    return acc;
  }, {});
  Object.keys(_localGroupedTasks).forEach(section => {
    _localGroupedTasks[section].sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));
  });
  const groupedTasks = sharedSchedule ? sharedSchedule.groupedTasks : _localGroupedTasks;

  const savedSectionNames = savedSections.map(s => s.section_name);
  const allSectionNames = new Set([...savedSectionNames, ...Object.keys(groupedTasks)]);
  const savedSet = new Set(savedSectionNames);
  const unsavedNames = Array.from(allSectionNames).filter(n => !savedSet.has(n)).sort();
  const _localSections = [...savedSectionNames.filter(n => allSectionNames.has(n)), ...unsavedNames];
  const sections = sharedSchedule ? sharedSchedule.sections : _localSections;
  sectionsRef.current = sections;

  // Display-layer views — these honor the schedule's search /
  // filter / sort settings so the Tasks panel stays in lockstep with
  // the Gantt panel. The drag-and-drop machinery above STILL operates
  // on the canonical `sections` + `groupedTasks` lists so reordering
  // works regardless of what's filtered out.
  const displayGroupedTasks =
    (sharedSchedule && sharedSchedule.filteredGroupedTasks) || groupedTasks;
  const displaySections =
    (sharedSchedule && sharedSchedule.filteredSections) || sections;

  // Auto-persist section order when there are unsaved sections (from tasks not yet in sections table)
  useEffect(() => {
    if (sectionsLoaded && unsavedNames.length > 0 && sections.length > 0 && projectId) {
      reorderScheduleSections(projectId, sections).then(updated => {
        setSavedSections(updated);
      }).catch(err => console.error('Error auto-saving section order:', err));
    }
  }, [sectionsLoaded, unsavedNames.length, projectId]); // eslint-disable-line

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // ============ PERCENT COMPLETE INLINE EDIT ============
  const handlePercentCompleteDoubleClick = (taskId, currentValue) => {
    setEditingPercentCompleteId(taskId);
    setEditingPercentCompleteValue(currentValue || 0);
  };

  const handlePercentCompleteEditSave = (taskId) => {
    const val = editingPercentCompleteValue;
    setEditingPercentCompleteId(null);
    setEditingPercentCompleteValue('');
    // Fire the update without awaiting — optimistic local state handles the UI instantly
    handlePercentCompleteChange(taskId, val);
  };

  const handlePercentCompleteEditCancel = () => {
    setEditingPercentCompleteId(null);
    setEditingPercentCompleteValue('');
  };

  // ============ XLSX TEMPLATE & IMPORT/EXPORT ============
  const handleDownloadTaskTemplate = () => {
    const wb = XLSX.utils.book_new();
    // predecessors + sov_links are the two new "one cell, many values"
    // columns added to the template. Format documented in the Field
    // Guide sheet and parsed client-side in handleImportTaskFile.
    const headers = [
      'task_code', 'task_name', 'task_description', 'building_area_phase',
      'task_type', 'start_date', 'finish_date', 'responsible_party',
      'client_visible', 'milestone_flag', 'predecessors', 'sov_links', 'notes', 'subtask_of',
    ];
    const sampleRows = [
      ['HS-001', 'Material Delivery',  'Deliver materials to site', 'High School', 'Procurement', '2026-04-01', '2026-04-05', 'John Smith', 'true',  'false', '',                        'HS-LABOR:25',                  'Example task'],
      ['HS-002', 'Demo Old Unit',      'Remove existing equipment', 'High School', 'Labor',       '2026-04-06', '2026-04-10', 'Jane Doe',   'true',  'false', 'HS-001',                  'HS-LABOR:25',                  ''],
      ['HS-003', 'Install New Unit',   'Install and connect',       'High School', 'Labor',       '2026-04-11', '2026-04-20', 'John Smith', 'true',  'false', 'HS-002:FS:2',             'HS-LABOR:50;HS-MAT:$15000',    'Example with lag + amount link'],
      ['HS-004', 'Startup',            'Its subtasks below',        'High School', 'Labor',       '',           '',           '',           'true',  'false', '',                        '',                             'A task with subtasks: no links of its own'],
      ['HS-005', 'Check refrigerant',  '',                          'High School', 'Labor',       '2026-04-21', '2026-04-22', 'John Smith', 'true',  'false', 'HS-003',                  '',                             '', 'HS-004'],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
    ws['!cols'] = [
      { wch: 12 }, { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 15 },
      { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 14 }, { wch: 14 },
      { wch: 22 }, { wch: 30 }, { wch: 30 }, { wch: 12 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Task Data');
    const legendData = [
      ['FIELD', 'VALID OPTIONS / FORMAT'], [''],
      ['task_type', 'Labor, Material, Procurement, Engineering, Inspection, Documents, Milestone'],
      ['start_date / finish_date', 'YYYY-MM-DD format (e.g., 2026-04-01)'],
      ['client_visible', 'true or false'],
      ['milestone_flag', 'true or false'],
      ['building_area_phase', 'Groups tasks into sections (e.g., High School, Junior High)'],
      [''],
      ['predecessors', 'Semicolon-separated list of task codes that must run before this task.'],
      ['', 'Each entry: TASK_CODE[:DEP_TYPE[:LAG_DAYS]].  DEP_TYPE default FS, lag default 0.'],
      ['', 'LAG_DAYS counts work days; add ed for calendar days that keep counting over weekends (7ed).'],
      ['', 'DEP_TYPE values: FS (finish-to-start), SS, FF, SF.'],
      ['', 'Examples:  HS-001         → FS, 0 lag'],
      ['', '            HS-001;HS-002 → two predecessors, both FS+0'],
      ['', '            HS-002:SS:2   → start-to-start, 2 days lag'],
      ['', '            HS-002:FS:7ed → finish-to-start, 7 calendar days lag (e.g. a concrete cure)'],
      ['', 'You can reference task codes defined in OTHER rows of this same import.'],
      [''],
      ['sov_links', 'Semicolon-separated list of SOV-line allocations attached to this task.'],
      ['', 'Each entry: SOV_CODE:VALUE. VALUE is a percentage by default (e.g., 50 = 50%).'],
      ['', 'Prefix the value with $ for a dollar amount instead (e.g., $25000).'],
      ['', 'Examples:  HS-LABOR:100              → 100% of HS-LABOR allocated here'],
      ['', '            HS-LABOR:50;HS-MAT:50     → split 50/50 across two SOV lines'],
      ['', '            HS-LABOR:25;HS-MAT:$15000 → 25% of HS-LABOR + $15,000 of HS-MAT'],
      ['', 'SOV codes must already exist on this project — create SOV lines first.'],
      [''],
      ['subtask_of', 'The task_code of the task this row is a subtask of, one level deep (e.g., HS-004). Empty for a task.'],
      ['', 'A task with subtasks only sums them up: its dates and % come from them, and its predecessors and'],
      ['', 'SOV links go on its subtasks. An arrow pointing at it, or a link on it, is refused, naming the row.'],
    ];
    const wsLegend = XLSX.utils.aoa_to_sheet(legendData);
    wsLegend['!cols'] = [{ wch: 24 }, { wch: 80 }];
    XLSX.utils.book_append_sheet(wb, wsLegend, 'Field Guide');
    XLSX.writeFile(wb, 'tasks_template.xlsx');
  };

  const parseTaskFileRows = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'xlsx' || ext === 'xls' || ext === 'xlsm') {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      return XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
    } else {
      const text = await file.text();
      const lines = text.split('\n').map(line => {
        const result = []; let current = ''; let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') inQuotes = !inQuotes;
          else if (char === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
          else current += char;
        }
        result.push(current.trim());
        return result;
      });
      if (lines.length < 2) return [];
      const headers = lines[0].map(h => h.toLowerCase().trim());
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const row = lines[i];
        if (!row || row.length < 2 || !row[0] || row[0].startsWith('---')) continue;
        const rowData = {};
        headers.forEach((header, idx) => { if (idx < row.length) rowData[header] = row[idx]; });
        rows.push(rowData);
      }
      return rows;
    }
  };

  const normalizeDate = (val) => {
    if (!val) return '';
    if (val instanceof Date) {
      return `${val.getFullYear()}-${String(val.getMonth() + 1).padStart(2, '0')}-${String(val.getDate()).padStart(2, '0')}`;
    }
    const str = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const match = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (match) return `${match[3]}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}`;
    return str;
  };

  // Parse the predecessors cell. Format:
  //   TASK_CODE[:DEP_TYPE[:LAG_DAYS]]; TASK_CODE; ...
  // Returns an array of { task_code, dependency_type, lag_days, lag_elapsed }. Throws
  // a row-scoped error on invalid dep_type so the import fails fast at
  // the parse step instead of getting a less helpful server-side error.
  const parsePredecessorsCell = (cell, rowLabel) => {
    if (cell == null || cell === '') return [];
    const VALID = new Set(['FS', 'SS', 'FF', 'SF']);
    return String(cell)
      .split(';')
      .map(s => s.trim())
      .filter(Boolean)
      .map(entry => {
        const parts = entry.split(':').map(p => p.trim());
        const task_code = parts[0];
        const dependency_type = (parts[1] || 'FS').toUpperCase();
        const lagText = parts[2] != null ? String(parts[2]).trim() : '';
        const lag_days = parseInt(lagText, 10) || 0;
        // "7ed" / "7 edays": calendar days that keep counting over weekends (#55).
        const lag_elapsed = /^-?\d+\s*e(d|days?)?$/i.test(lagText);
        if (!VALID.has(dependency_type)) {
          throw new Error(
            `${rowLabel}: predecessor '${entry}' has invalid dependency type '${parts[1]}' (must be FS, SS, FF, or SF)`,
          );
        }
        return { task_code, dependency_type, lag_days, lag_elapsed };
      });
  };

  // Parse the sov_links cell. Format:
  //   SOV_CODE:VALUE; SOV_CODE:VALUE; ...
  // Value is a percentage by default (50 = 50%). Prefix with $ for a
  // dollar amount (e.g., $25000). Returns an array shaped for the
  // server's bulk endpoint.
  const parseSovLinksCell = (cell, rowLabel) => {
    if (cell == null || cell === '') return [];
    return String(cell)
      .split(';')
      .map(s => s.trim())
      .filter(Boolean)
      .map(entry => {
        const colon = entry.indexOf(':');
        if (colon < 0) {
          throw new Error(
            `${rowLabel}: SOV link '${entry}' is missing a value (use SOV_CODE:PERCENT or SOV_CODE:$AMOUNT)`,
          );
        }
        const sov_code = entry.slice(0, colon).trim();
        const rawValue = entry.slice(colon + 1).trim();
        if (rawValue.startsWith('$')) {
          const amt = parseFloat(rawValue.slice(1).replace(/,/g, ''));
          if (isNaN(amt) || amt <= 0) {
            throw new Error(`${rowLabel}: SOV link '${entry}' has invalid amount`);
          }
          return { sov_code, allocation_method: 'Amount', allocation_amount: amt };
        }
        const pct = parseFloat(rawValue.replace(/%/g, ''));
        if (isNaN(pct) || pct <= 0) {
          throw new Error(`${rowLabel}: SOV link '${entry}' has invalid percent`);
        }
        return { sov_code, allocation_method: 'Percentage', allocation_percent: pct };
      });
  };

  const handleImportTaskFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setLoading(true);
      const rows = await parseTaskFileRows(file);
      if (rows.length === 0) {
        alert('File must have headers and at least one data row');
        return;
      }

      // Normalize headers and pre-filter rows missing both task_name and
      // task_code so the dialog's denominator reflects what's actually
      // going to be sent.
      const validRows = rows
        .map(rowData => {
          const r = {};
          Object.keys(rowData).forEach(k => { r[k.toLowerCase().trim()] = rowData[k]; });
          return r;
        })
        .filter(r => r.task_name || r.task_code);

      if (validRows.length === 0) {
        alert('No importable rows found (every row was missing both task_name and task_code).');
        return;
      }

      setImportProgress({ total: validRows.length, status: 'importing' });

      // Single bulk POST instead of one request per row. Avoids the
      // 500-req/min general rate limiter that previously 429'd the
      // project page right after a large schedule upload. Server wraps
      // every row in one transaction.
      const payload = validRows.map((row, idx) => {
        const startDate = normalizeDate(row.start_date);
        const finishDate = normalizeDate(row.finish_date);
        let duration = 0;
        if (startDate && finishDate) {
          duration = Math.max(
            1,
            Math.ceil((new Date(finishDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1,
          );
        }
        const cv = String(row.client_visible ?? 'true').toLowerCase();
        const mf = String(row.milestone_flag ?? 'false').toLowerCase();
        // rowLabel here uses idx+1 to match what server-side errors will
        // report ("Row N (code)") — keeps user-facing messages aligned
        // whether the failure happens in parse or in the POST handler.
        const rowLabel = `Row ${idx + 1} (${row.task_code || row.task_name})`;
        return {
          task_code: String(row.task_code || ''),
          task_name: String(row.task_name || ''),
          task_description: String(row.task_description || ''),
          building_area_phase: String(row.building_area_phase || ''),
          task_type: String(row.task_type || 'Work'),
          start_date: startDate,
          finish_date: finishDate,
          duration_days: duration,
          responsible_party: String(row.responsible_party || ''),
          client_visible: (cv === 'true' || cv === '1') ? 1 : 0,
          milestone_flag: (mf === 'true' || mf === '1') ? 1 : 0,
          notes: String(row.notes || ''),
          predecessors: parsePredecessorsCell(row.predecessors, rowLabel),
          sov_links: parseSovLinksCell(row.sov_links, rowLabel),
          // the task it's a subtask of: a code in this file or on the job (server/routes/tasks.js /bulk)
          parent_task_code: String(row.subtask_of ?? row['subtask of'] ?? '').trim() || undefined,
        };
      });

      const result = await bulkCreateTasks(projectId, payload);
      const importCount = result?.count ?? validRows.length;

      await fetchTasks();
      // Bubble up so the parent project page re-fetches its summary —
      // otherwise the header tiles that depend on task dates (Days
      // Remaining, project start/end) keep their pre-import nulls
      // until the user navigates away and back. SOVTab's bulk import
      // already does this same refresh.
      if (refreshSummary) refreshSummary();
      refreshTaskAlerts();
      setImportProgress({ total: validRows.length, count: importCount, status: 'success' });
      // No auto-dismiss — per the LoadingDialog spec, the complete
      // state IS the receipt. User clicks Done to dismiss.
    } catch (err) {
      setImportProgress(p => ({
        total: p?.total || 0,
        status: 'error',
        error: err.message || String(err),
      }));
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handleExportTasks = () => {
    const wb = XLSX.utils.book_new();
    // In the Schedule's tree order, with each subtask's task in "subtask_of" (the import reads it back).
    const codeById = new Map(tasks.map((x) => [x.id, x.task_code]));
    const inOrder = [...tasks].sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));
    const exportData = inOrder.map(t => ({
      task_code: t.task_code, task_name: t.task_name, task_description: t.task_description,
      building_area_phase: t.building_area_phase, task_type: t.task_type,
      start_date: t.start_date, finish_date: t.finish_date, duration_days: t.duration_days,
      percent_complete: t.percent_complete || 0, status: t.status || 'Not Started',
      responsible_party: t.responsible_party,
      client_visible: t.client_visible ? 'true' : 'false',
      milestone_flag: t.milestone_flag ? 'true' : 'false',
      notes: t.notes,
      subtask_of: t.parent_task_id != null ? codeById.get(t.parent_task_id) || '' : '',
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, 'Tasks');
    XLSX.writeFile(wb, 'tasks_export.xlsx');
  };

  // ============ SECTION & TASK ACTIONS ============
  const handleAddSection = () => {
    setNewSectionName('');
    setSectionNameError('');
    setShowAddSectionModal(true);
  };

  const handleConfirmAddSection = async () => {
    if (creatingSection) return;
    const trimmed = (newSectionName || '').trim();
    if (!trimmed) {
      setSectionNameError('Section name is required.');
      return;
    }
    const existingSections = getExistingSections();
    const exactMatch = existingSections.find(s => s === trimmed);
    if (exactMatch) {
      setSectionNameError(`A section named "${exactMatch}" already exists.`);
      return;
    }
    const caseMatch = existingSections.find(s => s.toLowerCase() === trimmed.toLowerCase());
    if (caseMatch) {
      setSectionNameError(`A section named "${caseMatch}" already exists (names are case-insensitive).`);
      return;
    }
    try {
      setCreatingSection(true);
      await createScheduleSection({ project_id: projectId, section_name: trimmed });
      await fetchSections();
      setShowAddSectionModal(false);
      setNewSectionName('');
      setSectionNameError('');
    } catch (error) {
      console.error('Error creating section:', error);
      setSectionNameError('Failed to create section. Please try again.');
    } finally {
      setCreatingSection(false);
    }
  };

  // Delete a section from the schedule. The schedule view groups tasks
  // by `task.building_area_phase` (free text on the task itself), so
  // deleting the normalized `sections` row does NOT delete tasks. Any
  // SOV lines that point at this section via section_id will be set to
  // NULL by the FK's ON DELETE SET NULL (i.e. they become "unsectioned"
  // in CE). We surface both facts in the confirm so the user knows
  // exactly what changes.
  const handleDeleteSection = async (sectionName) => {
    const sec = savedSections.find(
      s => (s.section_name || '').toLowerCase() === (sectionName || '').toLowerCase()
    );
    if (!sec) {
      // The section row only exists when it was either created via the
      // Add-Section UI / + Add new section… inline / SOV bulk import.
      // Section names that come purely from task.building_area_phase
      // (legacy or manually-typed) have no DB row to delete — for those
      // the user would have to clear the building_area_phase on each
      // task instead. Tell them rather than silently no-op'ing.
      await confirm({
        title: 'Cannot delete this section',
        message: `"${sectionName}" only exists because at least one task has it set as their Building / Area / Phase. Reassign or clear that on the affected tasks to make it disappear from the list.`,
        confirmLabel: 'OK',
        // hideCancel via danger:false + showing only confirm — useConfirm
        // doesn't expose a cancel-less mode, so this still has a Cancel
        // button. Both buttons just close.
      });
      return;
    }
    const taskCount = (groupedTasks[sectionName] || []).length;
    const taskWarning = taskCount > 0
      ? ` This section currently shows ${taskCount} task${taskCount === 1 ? '' : 's'} — those tasks won't be deleted, they'll keep their Building / Area / Phase value and re-appear under this name until you change it on each task.`
      : '';
    const ok = await confirm({
      title: `Delete section "${sec.section_name}"?`,
      message:
        `Any SOV lines assigned to this section will become "unsectioned" in CE (their data is preserved, only the grouping is removed).${taskWarning}`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteScheduleSection(sec.id);
      // Refresh savedSections so the section disappears immediately.
      // fetchSections is the existing pattern (used by inline-add).
      await fetchSections();
    } catch (err) {
      console.error('Error deleting section:', err);
      await confirm({
        title: 'Could not delete section',
        message: err?.message || String(err),
        confirmLabel: 'OK',
      });
    }
  };

  // "+ Add a subtask" (2026-10-29): Add Task already a subtask of this task, with its section and type, and its sub -- the
  // task's own while it has none, then the one its subtasks share.
  const openAddSubtask = (row) => {
    const parent = tasks.find((x) => x.id === row._addFor) || {};
    const subs = [...new Map(tasks.filter((x) => Number(x.parent_task_id) === parent.id && x.subcontractor_id)
      .map((x) => [x.subcontractor_id, x])).values()];
    const sub = parent.subcontractor_id ? parent : (subs.length === 1 ? subs[0] : null);
    handleAddTaskToSection(parent.building_area_phase || row.building_area_phase || '');
    setFormData((f) => ({ ...f, parent_task_id: parent.id, task_type: parent.task_type || 'Labor',
      responsible_party: sub ? sub.responsible_party || '' : '', subcontractor_id: sub ? sub.subcontractor_id : null }));
  };

  // The task window's "Add a subtask" (ScheduleView, 2026-11-02): Add Task, already a subtask of that task.
  useEffect(() => {
    if (addSubtaskFor == null) return;
    openAddSubtask({ _addFor: Number(addSubtaskFor) });
    if (onAddSubtaskOpened) onAddSubtaskOpened();
    // eslint-disable-next-line
  }, [addSubtaskFor]);

  const handleAddTaskToSection = (section) => {
    setArrowPicks({});
    setFormData({
      task_code: '', task_name: '', task_description: '', building_area_phase: section,
      task_type: 'Labor', start_date: '', finish_date: '', duration_days: 0, duration: '1',
      status: 'Not Started', percent_complete: 0,
      responsible_party: '', client_visible: true, milestone_flag: false, notes: ''
    });
    setFormErrors({});
    setPendingPredecessors([]);
    setPendingSovLinks([]);
    setShowModal(true);
  };

  // Add Task already filled in, for the Schedule's banners (2026-10-29, subs/SubReportsBanner.js: a sub's closeout
  // Documents task). Its section is the schedule's own spelling of it when there is one ("Closeout"), else a new one,
  // made when the task is.
  useEffect(() => {
    const ref = sharedSchedule && sharedSchedule.addTaskRef;
    if (!ref) return undefined;
    ref.current = (prefill) => {
      const want = String(prefill.building_area_phase || '').trim();
      const section = want ? (getExistingSections().find((x) => x.toLowerCase() === want.toLowerCase()) || want) : '';
      handleAddTaskToSection(section);
      setFormData((f) => ({ ...f, ...prefill, building_area_phase: section }));
    };
    return () => { ref.current = null; };
  });

  // Counts look at bottom rows only (subtasks, 2026-11-01): a parent sums its subtasks up.
  const parentIdSet = new Set(tasks.filter((x) => x.parent_task_id != null).map((x) => Number(x.parent_task_id)));
  const bottomRowCount = tasks.filter((x) => !parentIdSet.has(Number(x.id))).length;
  // A section line's count, as its rows show them: a closed parent counts its subtasks, an open one leaves them to their rows.
  const sectionRowCount = (list) => list.reduce((n, x) => n + (x._addFor ? 0 : x._kids ? (sharedSchedule?.collapsedParents?.[x.id] ? x._kids : 0) : 1), 0);

  // ============ EDIT (PROPERTIES) MODAL ============
  // When TasksTab is rendered inside ScheduleView (the combined
  // Schedule tab), the parent passes onTaskOpen to route editing
  // through its shared TaskEditModal -- which has the full
  // <TaskSOVLinks> add/remove UI. Without delegation, double-clicking
  // a task row in the list opened TasksTab's own inline modal, which
  // only shows a preview-only SOV Links section when allocations
  // already exist (no way to add a first link). That left users
  // unable to link a task to an SOV line from the Tasks list -- they
  // had to find the matching Gantt bar and double-click that instead,
  // which is non-obvious and broke the rolup flow.
  // When onTaskOpen is provided, defer to the parent. Otherwise fall
  // back to the local inline modal (legacy / standalone use).
  const handleEditTask = (task) => {
    if (onTaskOpen) {
      onTaskOpen(task.id);
      return;
    }
    setEditingTaskId(task.id);
    setEditFormData({
      task_code: task.task_code || '', task_name: task.task_name || '',
      task_description: task.task_description || '', building_area_phase: task.building_area_phase || '',
      task_type: task.task_type || 'Labor', start_date: task.start_date || '',
      finish_date: task.finish_date || '', duration_days: task.duration_days || 0,
      responsible_party: task.responsible_party || '', subcontractor_id: task.subcontractor_id || null,
      status: task.status || 'Not Started',
      client_visible: !!task.client_visible, milestone_flag: !!task.milestone_flag,
      notes: task.notes || '',
    });
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newData = { [name]: type === 'checkbox' ? checked : value };
    if (name === 'start_date' && value && !editFormData.finish_date) newData.finish_date = value;
    setEditFormData(prev => ({ ...prev, ...newData }));
  };

  const editDuration = (() => {
    if (editFormData.start_date && editFormData.finish_date) {
      const s = new Date(editFormData.start_date);
      const f = new Date(editFormData.finish_date);
      return Math.max(0, Math.ceil((f - s) / (1000 * 60 * 60 * 24)) + 1);
    }
    return editFormData.duration_days || 0;
  })();

  const handleSaveEdit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (savingEdit) return;
    console.log('handleSaveEdit called', { editingTaskId, editFormData });
    // Validate section name — prevent near-duplicate names (e.g., "elementary school" vs "Elementary School")
    const originalTask = tasks.find(t => t.id === editingTaskId);
    const newSection = (editFormData.building_area_phase || '').trim();
    const originalSection = originalTask ? (originalTask.building_area_phase || '').trim() : '';
    if (newSection && newSection !== originalSection) {
      const existingSections = getExistingSections();
      // Check if the exact name already exists (this is fine — it just moves the task into that section)
      const exactMatch = existingSections.find(s => s === newSection);
      if (!exactMatch) {
        // No exact match — check for case-insensitive match which would create a confusing near-duplicate
        const caseMatch = existingSections.find(s => s.toLowerCase() === newSection.toLowerCase());
        if (caseMatch) {
          alert(`A section named "${caseMatch}" already exists. Did you mean to use that exact name? Section names must be unique (case-insensitive).`);
          return;
        }
      }
    }
    // Enforce: if task is scheduled entirely in the future, completion must be 0%
    // This protects billing integrity — can't bill for work on days that haven't happened
    const today = testDate || new Date().toISOString().split('T')[0];
    const newStart = editFormData.start_date;
    const newEnd = editFormData.finish_date;
    const origTask = tasks.find(tk => tk.id === editingTaskId);
    const pct = parseFloat(origTask?.percent_complete) || 0;
    const bothDatesInFuture = newStart && newEnd && newStart > today && newEnd > today;
    console.log('[SaveEdit] Date check:', { today, newStart, newEnd, pct, bothDatesInFuture, origPct: origTask?.percent_complete });

    // Proration guard: cap progress at what the schedule allows as of today
    const maxPctForEdit = getMaxProgress({ start_date: newStart, finish_date: newEnd });
    if (pct > maxPctForEdit) {
      const cappedPct = maxPctForEdit;
      if (cappedPct === 0) {
        await confirm({
          title: 'Completion Reset Required',
          message: `This task has ${pct}% completion but is now scheduled entirely in the future (starts ${newStart}). Completion will be reset to 0% because work cannot be billed for dates that haven't occurred yet.`,
          confirmLabel: 'OK',
          danger: false,
        });
      } else {
        await confirm({
          title: 'Progress Will Be Capped',
          message: `This task is at ${pct}% but the schedule (${newStart} – ${newEnd}) only supports ${cappedPct}% based on today's date. Progress will be reduced to ${cappedPct}%.`,
          confirmLabel: 'OK',
          danger: false,
        });
      }
    }

    try {
      setSavingEdit(true);
      const saveData = {
        ...editFormData,
        duration_days: editDuration,
        client_visible: editFormData.client_visible ? 1 : 0,
        milestone_flag: editFormData.milestone_flag ? 1 : 0,
      };
      // Enforce proration: cap percent_complete at the schedule-prorated max
      if (pct > maxPctForEdit) {
        saveData.percent_complete = maxPctForEdit;
        saveData.status = maxPctForEdit === 0 ? 'Not Started' : maxPctForEdit >= 100 ? 'Complete' : 'In Progress';
      }
      console.log('[SaveEdit] Saving:', { percent_complete: saveData.percent_complete, status: saveData.status, start: saveData.start_date, end: saveData.finish_date });
      await updateTask(editingTaskId, saveData);
      const savedTaskId = editingTaskId;
      setEditingTaskId(null);
      setEditFormData({});
      fetchTasks({ preserveScroll: true });
      refreshSummary();
      // Optimistic flag clear so the amber border drops the moment the
      // modal closes, even if the debounced server refresh is pending.
      if ((parseFloat(saveData.percent_complete) || 0) >= 100) {
        clearFlagLocally(savedTaskId);
      }
      refreshTaskAlerts();
    } catch (err) {
      console.error('Error updating task:', err);
      alert('Failed to save: ' + err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  // Get SOV link names for display
  const getSOVLinkNames = (task) => {
    const allocs = taskAllocations[task.id];
    if (allocs && allocs.length > 0) {
      return allocs.map(a => a.sov_line_description || a.sov_code).join(', ');
    }
    return '';
  };

  // ============ RENDER ============
  // Six columns: drag, code, task name, type, dates, % done.
  // Status / SOV Links / SOV-link icon / edit icon were removed — double-click
  // a row to open the Task properties modal.
  // 6-column grid. Owner intentionally OMITTED — that's drill-in info
  // surfaced in the Task Properties modal, not scan-the-list info.
  // Reclaiming the column keeps the row dense with high-signal columns:
  // task name (wide), type, dates+duration, progress.
  const gridCols = TASK_GRID_COLS;

  // Dark-mode-aware row-tint helpers — same shape as BillingTab. Light
  // literals (#fef2f2 / #fffbeb / #fef3c7) glow white against the dark
  // canvas and wash out the light task-row text. Semi-transparent
  // accent overlays keep the tint subtle.
  const rowAlertBg = (severity) => {
    if (t.dark) {
      return severity === 'critical' ? 'rgba(239, 68, 68, 0.14)' : 'rgba(245, 158, 11, 0.12)';
    }
    return severity === 'critical' ? '#fef2f2' : '#fffbeb';
  };
  const rowAlertHoverBg = (severity) => {
    if (t.dark) {
      return severity === 'critical' ? 'rgba(239, 68, 68, 0.22)' : 'rgba(245, 158, 11, 0.22)';
    }
    return '#fef3c7';
  };

  // ── Action bar pieces (rendered in the toolbar, in the ⋮ menu, and in the hidden
  //    measuring copy; see computeFold) ──
  const scheduleFilterNow = sharedSchedule?.scheduleFilter
    || { sections: new Set(), statuses: new Set(), types: new Set() };
  const filterCount = scheduleFilterNow.sections.size + scheduleFilterNow.statuses.size + scheduleFilterNow.types.size;
  const filterLabel = filterCount > 0 ? `Filter (${filterCount})` : 'Filter';
  const sortKeyNow = sharedSchedule?.scheduleSort || 'default';
  const sortLabel = sortKeyNow === 'default'
    ? 'Sort'
    : `Sort: ${SCHEDULE_SORT_OPTIONS.find((o) => o.key === sortKeyNow)?.label || ''}`;
  // From the toolbar a click toggles; from the ⋮ menu it always opens, under the menu button.
  const openFilterMenu = (rect, align, fromMenu = false) => {
    setFilterMenuAnchor(rect);
    setPopoverAlign(align);
    setFilterMenuOpen((v) => (fromMenu ? true : !v));
    setSortMenuOpen(false);
  };
  const openSortMenu = (rect, align, fromMenu = false) => {
    setSortMenuAnchor(rect);
    setPopoverAlign(align);
    setSortMenuOpen((v) => (fromMenu ? true : !v));
    setFilterMenuOpen(false);
  };
  const moreRect = () => moreRef.current && moreRef.current.getBoundingClientRect();
  // First paint, and whenever a label or the task count changes width: re-fold
  // before paint (not left to the ResizeObserver, which a background tab defers).
  useLayoutEffect(() => { computeFold(); }, [computeFold, filterLabel, sortLabel, tasks.length]);

  // Search field with ⌘K kbd hint (at its minimum width once anything folds).
  const renderSearch = (compact, measuring = false) => (
    <div style={{
      display: 'inline-flex', alignItems: 'center', height: 28,
      padding: '0 8px', gap: 6,
      background: 'var(--bg-surface-2)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 6, minWidth: SEARCH_MIN_W, maxWidth: 240,
      ...(compact ? { width: SEARCH_MIN_W } : {}),
    }}>
      <Icon name="search" size={13} style={{ color: 'var(--text-3)' }} />
      <input
        type="text"
        value={sharedSchedule?.scheduleQuery ?? ''}
        onChange={measuring ? undefined : (e) => sharedSchedule?.setScheduleQuery?.(e.target.value)}
        readOnly={measuring}
        tabIndex={measuring ? -1 : undefined}
        placeholder="Search tasks…"
        style={{
          flex: 1, border: 'none', outline: 'none', background: 'transparent',
          color: 'var(--text-1)', fontFamily: 'var(--font-sans)', fontSize: 13.5, minWidth: 0,
        }}
      />
      {sharedSchedule?.scheduleQuery
        ? (
          <span
            onClick={() => sharedSchedule?.setScheduleQuery?.('')}
            title="Clear search"
            style={{ cursor: 'pointer', color: 'var(--text-3)', fontSize: 15.5, lineHeight: 1 }}
          >×</span>
        )
        : (
          <kbd className="mono" style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            height: 18, padding: '0 5px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 4, fontSize: 11.5, color: 'var(--text-3)',
          }}>⌘K</kbd>
        )
      }
    </div>
  );

  const renderToolbarItem = (key, measuring = false) => {
    switch (key) {
      // Filter / Sort: state + popover JSX live in TasksTab; filter+sort values flow up
      // to ScheduleView via sharedSchedule so both this panel AND the Gantt rebuild
      // their visible rows in lockstep.
      case 'filter':
        return <GhostButton icon="filter" label={filterLabel} onClick={(e) => openFilterMenu(e.currentTarget.getBoundingClientRect(), 'left')} />;
      case 'sort':
        return <GhostButton icon="sort" label={sortLabel} onClick={(e) => openSortMenu(e.currentTarget.getBoundingClientRect(), 'left')} />;
      // Icon-only utility buttons (was text-labeled). Tooltips carry the verb so we
      // don't lose discoverability.
      case 'template':
        return <IconUtilBtn name="template" label="Template" onClick={handleDownloadTaskTemplate} />;
      case 'export':
        return <IconUtilBtn name="download" label="Export tasks" onClick={handleExportTasks} />;
      case 'import':
        return (
          <label
            title="Import tasks"
            aria-label="Import tasks"
            style={{
              width: 28, height: 28, borderRadius: 6, border: 'none',
              background: 'transparent', color: 'var(--text-3)',
              cursor: 'pointer', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', padding: 0,
              transition: 'background .12s, color .12s', marginBottom: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-3)'; }}
          >
            <Icon name="upload" size={14} />
            {!measuring && <input type="file" accept=".xlsx,.xls,.xlsm,.csv" onChange={handleImportTaskFile} style={{ display: 'none' }} />}
          </label>
        );
      case 'print':
        return <IconUtilBtn name="printer" label="Print schedule" onClick={sharedSchedule?.openPrintModal} />;
      case 'section':
        return <button className="btn btn-secondary" onClick={handleAddSection} style={{ padding: '5px 10px', fontSize: '13.5px', whiteSpace: 'nowrap' }}>+ Section</button>;
      case 'task':
        return <button className="btn btn-primary" onClick={() => setShowModal(true)} style={{ padding: '5px 12px', fontSize: '13.5px', whiteSpace: 'nowrap' }}>+ Task</button>;
      default:
        return null;
    }
  };

  // The ⋮ menu: only what's folded, in toolbar order, grouped like the toolbar.
  const menuIcon = (name) => <span style={{ display: 'inline-flex', color: 'var(--text-3)' }}><Icon name={name} size={14} /></span>;
  const toolbarMenuItems = {
    filter: { label: filterLabel, icon: menuIcon('filter'), onClick: () => openFilterMenu(moreRect(), 'right', true) },
    sort: { label: sortLabel, icon: menuIcon('sort'), onClick: () => openSortMenu(moreRect(), 'right', true) },
    template: { label: 'Template', icon: menuIcon('template'), onClick: handleDownloadTaskTemplate },
    export: { label: 'Export tasks', icon: menuIcon('download'), onClick: handleExportTasks },
    import: { label: 'Import tasks', icon: menuIcon('upload'), onClick: () => importInputRef.current && importInputRef.current.click() },
    print: { label: 'Print schedule', icon: menuIcon('printer'), onClick: () => sharedSchedule?.openPrintModal?.() },
    section: { label: 'Add section', icon: menuIcon('plus'), onClick: handleAddSection },
    task: { label: 'Add task', icon: menuIcon('plus'), onClick: () => setShowModal(true) },
  };
  const foldedMenuItems = [['filter', 'sort'], ['template', 'export', 'import', 'print'], ['section', 'task']]
    .map((group) => group.filter((k) => folded.includes(k)).map((k) => toolbarMenuItems[k]))
    .filter((group) => group.length > 0)
    .flatMap((group, i) => (i > 0 ? [{ divider: true }, ...group] : group));

  return (
    <div className="tasks-tab">
      {/* Header — Schedule action bar per handoff §4. 60px tall (matches
          GanttTab's chrome for clean horizontal alignment across the
          split-panel seam). Title + count on left, search + ghost
          filter/sort + icon-only utility buttons + primary +Section /
          +Task on right; what doesn't fit folds into a ⋮ menu (computeFold). */}
      <div ref={headerRef} style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'nowrap', height: '60px', boxSizing: 'border-box', borderBottom: '1px solid var(--border-subtle)', padding: '0 16px' }}>
        <div ref={titleRef} style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>Schedule Tasks</span>
          <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)' }}>{bottomRowCount}</span>
        </div>
        <div style={{ display: 'flex', gap: TOOLBAR_GAP, alignItems: 'center', flexWrap: 'nowrap' }}>
          {renderSearch(folded.length > 0)}
          {toolbarKeys.filter((k) => !folded.includes(k)).map((k) => (
            <React.Fragment key={k}>{renderToolbarItem(k)}</React.Fragment>
          ))}
          {folded.length > 0 && (
            <span ref={moreRef} style={{ display: 'inline-flex' }}>
              <Menu
                trigger={(toggle) => <IconUtilBtn name="kebab" label="More actions" onClick={toggle} />}
                items={foldedMenuItems}
              />
            </span>
          )}
        </div>
        {/* Import from the ⋮ menu (the toolbar's own Import button keeps its input). */}
        <input ref={importInputRef} type="file" accept=".xlsx,.xls,.xlsm,.csv" onChange={handleImportTaskFile} style={{ display: 'none' }} />
        {dragHint && (
          <div role="status" style={{ position: 'fixed', left: '50%', bottom: 76, transform: 'translateX(-50%)', zIndex: 4000, maxWidth: 560,
            background: 'var(--text-1)', color: 'var(--bg-surface)', padding: '10px 16px', borderRadius: 10, fontSize: 13.5, lineHeight: 1.45,
            boxShadow: '0 8px 24px oklch(0.2 0.02 90 / 0.25)' }}>{dragHint}</div>
        )}
        {/* Hidden copy of the toolbar at natural widths, read by computeFold. The 0x0
            clip keeps it out of the panel's scroll width. */}
        <div aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, width: 0, height: 0, overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' }}>
          <div ref={measureRef} style={{ display: 'flex', gap: TOOLBAR_GAP, alignItems: 'center', width: 'max-content' }}>
            <span data-tb="search" style={{ display: 'inline-flex', flexShrink: 0 }}>{renderSearch(false, true)}</span>
            {toolbarKeys.map((k) => (
              <span key={k} data-tb={k} style={{ display: 'inline-flex', flexShrink: 0 }}>{renderToolbarItem(k, true)}</span>
            ))}
          </div>
        </div>
      </div>

      {((sharedSchedule ? sharedSchedule.loading : loading) && tasks.length === 0) ? (
        <LoadingState message="Loading tasks…" />
      ) : (
        <div style={{ minWidth: TASK_LIST_MIN_W }}>
          {/* Column headers — height 52 + 1px border = 53 total, matching the
              Gantt's stickyHeaderRow (52px child + 1px border = 53). */}
          <div style={{
            display: 'grid', gridTemplateColumns: gridCols, columnGap: '12px',
            height: '52px', boxSizing: 'content-box', alignItems: 'center', padding: '0 12px',
            background: 'var(--bg-surface-2)',
            borderBottom: '1px solid var(--border-subtle)',
            fontSize: 12, fontWeight: 500, textTransform: 'uppercase',
            color: 'var(--text-3)', letterSpacing: '0.04em',
          }}>
            <div></div>
            <div>Code</div>
            <div>Task</div>
            <div>Type</div>
            <div>Dates</div>
            <div>% Done</div>
          </div>

          {/* The search (the project's top bar, or this box: one text) hid every task. */}
          {displaySections.length === 0 && sections.length > 0 && (sharedSchedule?.scheduleQuery || '').trim() && (
            <div style={{ padding: '28px 16px', textAlign: 'center', color: t.textMuted, fontSize: '14.5px', borderBottom: '1px solid var(--border-subtle)' }}>
              {noMatchText(sharedSchedule.scheduleQuery).body}
            </div>
          )}
          {displaySections.map(section => (
            <div
              key={section}
              onDragOver={(e) => { if (draggedSectionName) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverSectionName(section); } }}
              onDrop={(e) => { if (draggedSectionName) handleSectionDrop(e, section); }}
              style={{
                // Use inset box-shadow for the drag indicator instead of a
                // 3px transparent border — borders take up layout space even
                // when transparent, which added 3px per section of accumulated
                // drift between the Tasks list and the Gantt rows.
                boxShadow: dragOverSectionName === section && draggedSectionName && draggedSectionName !== section
                  ? 'inset 0 3px 0 0 #3b82f6'
                  : 'none',
                transition: 'box-shadow 0.15s',
              }}
            >
              {/* Section header (Duncan 2026-10-30, the PM meeting -- Johnny wanted a section's % and dates; then "more emphasis on
                  the headers ... add a little bit of color to the header line item", its dates and % "very squished"): a tinted
                  line in the list's own columns -- its name, count, + Task and delete; its dates in Dates and its % (its tasks by
                  working days, lib/rollup.js) in % Done. */}
              {(() => {
                const list = displayGroupedTasks[section] || [];
                const { pct, start: minS, finish: maxE } = rollUp((sharedSchedule?.groupedTasks || {})[section] || list, workWeek);
                const rowsOnly = list.filter((x) => !x._addFor);
                const allComplete = rowsOnly.length > 0 && rowsOnly.every((x) => (parseFloat(x.percent_complete ?? x.progress) || 0) >= 100);
                const over = dragOverSectionName === section && draggedSectionName && draggedSectionName !== section;
                return (
                  <div
                    draggable
                    onDragStart={(e) => handleSectionDragStart(e, section)}
                    onDragEnd={handleSectionDragEnd}
                    onClick={() => { if (!draggedSectionName) toggleSection(section); }}
                    title="Click to open or close · drag to reorder"
                    style={{
                      display: 'grid', gridTemplateColumns: gridCols, columnGap: '12px', alignItems: 'center',
                      height: '40px', boxSizing: 'border-box', padding: '0 12px',
                      background: over ? 'var(--accent-soft)' : 'color-mix(in oklch, var(--accent) 11%, var(--bg-surface))',
                      borderLeft: '3px solid var(--accent)',
                      borderBottom: '1px solid color-mix(in oklch, var(--accent) 25%, var(--border-subtle))',
                      cursor: 'grab', transition: 'background-color 0.15s',
                    }}
                  >
                    <span style={{ fontSize: 12.5, color: 'var(--accent-text)', textAlign: 'center', userSelect: 'none' }}>{collapsedSections[section] ? '▶' : '▼'}</span>
                    <div style={{ gridColumn: 'span 3', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <span aria-hidden title={allComplete ? 'All tasks complete' : 'In progress'}
                        style={{ width: 8, height: 8, borderRadius: 999, background: allComplete ? 'var(--success)' : 'var(--accent)', flexShrink: 0 }} />
                      <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: '-0.01em', color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {section}
                      </span>
                      <span className="mono" style={{ color: 'var(--accent-text)', fontSize: 12.5, fontWeight: 600, flexShrink: 0 }}>{sectionRowCount(list)}</span>
                      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <button className="btn btn-secondary" title={`Add a task to ${section}`}
                          onClick={(e) => { e.stopPropagation(); handleAddTaskToSection(section); }}
                          style={{ fontSize: '12.5px', padding: '2px 9px' }}>+ Task</button>
                        {/* Delete-section control: confirms first (handleDeleteSection); only the sections-table row goes -- the
                            tasks stay put. A red trash can, not the old 24 px "×" (2026-11-02, a PM: "the delete button for
                            sections is tiny"; Duncan's pick B). */}
                        <button type="button" aria-label={`Delete section ${section}`} title="Delete section"
                          onClick={(e) => { e.stopPropagation(); handleDeleteSection(section); }}
                          style={{
                            background: 'var(--bg-surface)', border: '1px solid color-mix(in oklch, var(--danger) 40%, transparent)',
                            color: 'var(--danger)', borderRadius: 7, width: 30, height: 26, cursor: 'pointer', padding: 0,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}>
                          <Icon name="trash" size={16} stroke={2} />
                        </button>
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                      {minS && (
                        <>
                          <div className="mono" style={{ fontSize: 12.5, color: 'var(--text-1)', fontWeight: 600, whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
                            {fmtCompactDate(minS)} <span style={{ color: 'var(--text-4)' }}>{'→'}</span> {fmtCompactDate(maxE)}
                          </div>
                          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{spanDays(minS, maxE, workWeek)} days</div>
                        </>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} title="This section's tasks, counted by working days">
                      {pct != null && (
                        <>
                          <div style={{ width: '50px', height: '5px', background: 'color-mix(in oklch, var(--text-3) 22%, transparent)', borderRadius: 999, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: pct >= 100 ? 'var(--success)' : 'var(--accent)', borderRadius: 999 }} />
                          </div>
                          <span className="mono" style={{ fontSize: '12.5px', color: 'var(--text-1)', fontWeight: 700 }}>{pct}%</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Empty section placeholder */}
              {!collapsedSections[section] && (!displayGroupedTasks[section] || displayGroupedTasks[section].length === 0) && (
                <div style={{ padding: '20px 12px', textAlign: 'center', color: t.textMuted, fontSize: '14.5px', borderBottom: '1px solid var(--border-subtle)' }}>
                  No tasks yet — click "+ Add Task" above to get started
                </div>
              )}
              {/* Task rows */}
              {!collapsedSections[section] && (displayGroupedTasks[section] || []).map((task, idx) => {
                if (task._addFor) return ( // another subtask of this task (ScheduleView's renest puts this row under its subtasks)
                  <div key={task.id} onDragOver={(e) => handleDragOver(e, task)} onDrop={(e) => handleDrop(e, task, section)}
                    style={{ display: 'grid', gridTemplateColumns: gridCols, columnGap: '12px', height: '40px', boxSizing: 'border-box', padding: '0 12px', alignItems: 'center', borderBottom: '1px solid var(--border-subtle)', borderLeft: '3px solid transparent' }}>
                    <div /><div />
                    <div style={{ paddingLeft: 22 }}>
                      <button type="button" onClick={() => openAddSubtask(task)} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 13.5, fontWeight: 500, fontFamily: 'inherit' }}>+ Add a subtask</button>
                    </div>
                  </div>
                );
                // A parent's % is its subtasks', kept to 2 decimals (server/subtasks.js): shown whole.
                const pct = task._kids ? Math.round(Number(task.percent_complete) || 0) : (task.percent_complete || 0);
                const hasAllocations = taskAllocations[task.id] && taskAllocations[task.id].length > 0;
                const duration = spanDays(task.start_date, task.finish_date, workWeek) || task.duration_days || 0;
                const validPredecessors = (task.predecessors || []).filter(p => p.predecessor_task_id != null);
                const hasPredecessors = validPredecessors.length > 0;
                const successorTasks = tasks.filter(t => (t.predecessors || []).some(p => p.predecessor_task_id != null && Number(p.predecessor_task_id) === Number(task.id)));
                const isPrereqForOthers = successorTasks.length > 0;
                const isDragOver = dragOverTask === task.id;
                const depFlagged = (depAlertData.flagged_task_ids || []).includes(task.id);
                const taskDepAlerts = depFlagged ? (depAlertData.alerts || []).filter(a => a.task_id === task.id || a.related_task_id === task.id) : [];
                // A sub moved this task's dates on E3 Subs and it waits on the PM (2026-10-24): amber, as a flag.
                const subMove = sharedSchedule?.subMoves?.[task.id];
                // ... and its sub's progress waiting on the PM (2026-10-28): amber too, with a pill that opens the task at it.
                const subProg = sharedSchedule?.subProgress?.[task.id];
                const amber = flaggedTaskIds.has(task.id) || !!subMove || !!subProg;

                return (
                  <React.Fragment key={task.id}>
                    <div
                      className="sched-row"
                      draggable
                      onDragStart={(e) => handleDragStart(e, task, section)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, task)}
                      onDrop={(e) => handleDrop(e, task, section)}
                      onDoubleClick={() => handleEditTask(task)}
                      style={{
                        display: 'grid', gridTemplateColumns: gridCols, columnGap: '12px',
                        height: '40px', boxSizing: 'border-box', padding: '0 12px', alignItems: 'center', fontSize: '14.5px',
                        borderBottom: '1px solid var(--border-subtle)',
                        borderTop: isDragOver ? '2px solid #3b82f6' : 'none',
                        borderLeft: depFlagged ? (taskDepAlerts.some(a => a.severity === 'critical') ? '3px solid #dc2626' : '3px solid #f59e0b') : amber ? '3px solid #f59e0b' : '3px solid transparent',
                        backgroundColor: depFlagged
                          ? rowAlertBg(taskDepAlerts.some(a => a.severity === 'critical') ? 'critical' : 'warning')
                          : amber
                            ? rowAlertBg('warning')
                            : 'transparent',
                        transition: 'background-color 0.15s',
                        cursor: 'default',
                        userSelect: 'none',
                      }}
                      onMouseEnter={(e) => {
                        const sev = depFlagged
                          ? (taskDepAlerts.some(a => a.severity === 'critical') ? 'critical' : 'warning')
                          : (amber ? 'warning' : null);
                        e.currentTarget.style.backgroundColor = sev
                          ? rowAlertHoverBg(sev)
                          : (t.dark ? 'rgba(255,255,255,0.03)' : '#f8fafc');
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = depFlagged
                          ? rowAlertBg(taskDepAlerts.some(a => a.severity === 'critical') ? 'critical' : 'warning')
                          : amber
                            ? rowAlertBg('warning')
                            : 'transparent';
                      }}
                    >
                      {/* Drag handle - 6 dot grip (a task with subtasks opens and closes them here; a subtask has none: it stays in
                          date order under its task) */}
                      {task._kids ? (
                        <button type="button" onClick={(e) => { e.stopPropagation(); sharedSchedule?.toggleParent?.(task.id); }} onDoubleClick={(e) => e.stopPropagation()}
                          title={sharedSchedule?.collapsedParents?.[task.id] ? 'Show its subtasks' : 'Hide its subtasks'} aria-label={sharedSchedule?.collapsedParents?.[task.id] ? 'Show its subtasks' : 'Hide its subtasks'}
                          style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-3)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {sharedSchedule?.collapsedParents?.[task.id] ? '▶' : '▼'}
                        </button>
                      ) : task._depth ? <div /> : (
                      <div style={{ cursor: 'grab', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px 0' }}>
                        <svg width="10" height="16" viewBox="0 0 10 16" fill="none">
                          <circle cx="3" cy="2" r="1.3" fill="#cbd5e1" />
                          <circle cx="7" cy="2" r="1.3" fill="#cbd5e1" />
                          <circle cx="3" cy="8" r="1.3" fill="#cbd5e1" />
                          <circle cx="7" cy="8" r="1.3" fill="#cbd5e1" />
                          <circle cx="3" cy="14" r="1.3" fill="#cbd5e1" />
                          <circle cx="7" cy="14" r="1.3" fill="#cbd5e1" />
                        </svg>
                      </div>
                      )}

                      {/* Code (redesign: mono, text-4, smaller — was 12px t.textSecondary); a subtask's place under its task, .1 */}
                      <div className="mono" title={task.task_code} style={{ color: 'var(--text-4)', fontWeight: 500, fontSize: '12.5px', letterSpacing: '-0.02em', paddingLeft: task._depth ? 14 : 0, whiteSpace: 'nowrap' }}>{task._depth ? String(task.task_code || '').slice(String(task.task_code || '').lastIndexOf('.')) : task.task_code}</div>

                      {/* Task name + link indicators */}
                      {/* The name keeps room (Duncan 2026-10-28: a narrow list squeezed it to one letter): the name and its small
                          icons on the first line, at least 140px; the sub's pills and technicians after them, dropping to a
                          second line when there's no room beside the name. */}
                      <div style={{ fontWeight: 500, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: '6px', rowGap: task._depth ? '2px' : '3px', color: t.heading, minWidth: 0, position: 'relative', paddingLeft: task._depth ? 22 : 0, lineHeight: task._depth ? 1.2 : undefined }}>
                        {task._depth ? <span aria-hidden style={{ position: 'absolute', left: 5, top: 3, width: 10, height: 9, borderLeft: '1.5px solid var(--border-strong, #cbd5e1)', borderBottom: '1.5px solid var(--border-strong, #cbd5e1)', borderBottomLeftRadius: 4 }} /> : null}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flex: '0 1 auto', minWidth: 'min(140px, 100%)', maxWidth: '100%' }}>
                        <span title={task.task_name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, fontWeight: task._depth ? 400 : undefined }}>{task.task_name}</span>
                        {task._kids ? <span className="mono" title={`${task._kids} subtasks: its dates and % come from them`} style={{ color: 'var(--text-3)', fontSize: 12.5, fontWeight: 500, flexShrink: 0 }}>{task._kids}</span> : null}
                        {/* "+ Subtask" (Duncan 2026-11-02: no clean way to give a task its first subtask): shown while the row is
                            pointed at (App.css .sched-row); Add Task, already a subtask of it. Not on a milestone, a subtask, or a
                            task with fixture lines (it breaks down one way: server/subtasks.js). */}
                        {!task._depth && task.parent_task_id == null && !Number(task.milestone_flag) && !task.has_work && (
                          <button type="button" className="add-sub-chip" title={`Add a subtask to ${task.task_name}`}
                            onClick={(e) => { e.stopPropagation(); openAddSubtask({ _addFor: task.id }); }} onDoubleClick={(e) => e.stopPropagation()}
                            style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)', zIndex: 2, fontSize: 12, fontWeight: 600, lineHeight: '18px', padding: '0 8px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                              color: 'var(--accent-text)', background: 'color-mix(in oklch, var(--accent) 10%, var(--bg-surface))', border: '1px solid color-mix(in oklch, var(--accent) 40%, transparent)' }}>
                            + Subtask
                          </button>
                        )}
                        {/* Its sub's to work, with no work on it yet -- or a Documents task past its start with none
                            yet (2026-10-24; server/workNeeded.js) */}
                        {(task.needs_work || needsDocs(task, testDate || todayYmd(), sharedSchedule?.documents?.[task.id])) && (
                          <span title={task.needs_work ? 'No work uploaded yet. Open the task on the Subcontractors tab to import its workbook.'
                            : 'No documents yet. The sub uploads them on E3 Subs.'}
                            style={{ flex: '0 0 auto', display: 'inline-flex', color: 'var(--warning)', cursor: 'help' }}>
                            <Icon name="alert-triangle" size={14} stroke={2} />
                          </span>
                        )}
                        {depFlagged && (
                          <span
                            title={taskDepAlerts.map(a => a.message).join('\n') || 'Scheduling alert'}
                            style={{
                              fontSize: '14.5px',
                              cursor: 'help',
                              color: taskDepAlerts.some(a => a.severity === 'critical') ? '#dc2626' : '#d97706',
                            }}
                          >&#9888;</span>
                        )}
                        {isPrereqForOthers && (
                          <span className="dep-tooltip-wrap" style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: 'pointer' }}>
                              <line x1="5" y1="12" x2="19" y2="12" />
                              <polyline points="12 5 19 12 12 19" />
                            </svg>
                            <div className="dep-tooltip" style={{
                              display: 'none', position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                              marginBottom: '6px', padding: '8px 12px', backgroundColor: '#1e293b', color: '#f8fafc',
                              borderRadius: '8px', fontSize: '13.5px', whiteSpace: 'nowrap', zIndex: 1000,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', pointerEvents: 'none',
                            }}>
                              <div style={{ fontWeight: 600, color: '#c4b5fd', marginBottom: successorTasks.length > 1 ? '4px' : '0' }}>Dependents:</div>
                              {successorTasks.map(s => (
                                <div key={s.id} style={{ padding: '1px 0' }}>
                                  <span style={{ color: '#c4b5fd', fontWeight: 600 }}>{s.task_code}</span>
                                  <span style={{ color: t.cardBorder }}> {s.task_name}</span>
                                </div>
                              ))}
                              <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                                width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1e293b' }} />
                            </div>
                          </span>
                        )}
                        {hasPredecessors && (
                          <span className="dep-tooltip-wrap" style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: 'pointer' }}>
                              <line x1="19" y1="12" x2="5" y2="12" />
                              <polyline points="12 19 5 12 12 5" />
                            </svg>
                            <div className="dep-tooltip" style={{
                              display: 'none', position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                              marginBottom: '6px', padding: '8px 12px', backgroundColor: '#1e293b', color: '#f8fafc',
                              borderRadius: '8px', fontSize: '13.5px', whiteSpace: 'nowrap', zIndex: 1000,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', pointerEvents: 'none',
                            }}>
                              <div style={{ fontWeight: 600, color: '#6ee7b7', marginBottom: validPredecessors.length > 1 ? '4px' : '0' }}>Predecessors:</div>
                              {validPredecessors.map(p => {
                                const pt = tasks.find(t => Number(t.id) === Number(p.predecessor_task_id));
                                return (
                                  <div key={p.id || p.predecessor_task_id} style={{ padding: '1px 0' }}>
                                    <span style={{ color: '#6ee7b7', fontWeight: 600 }}>{pt ? pt.task_code : '?'}</span>
                                    <span style={{ color: '#e2e8f0' }}> {pt ? pt.task_name : 'Unknown'}</span>
                                  </div>
                                );
                              })}
                              <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                                width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1e293b' }} />
                            </div>
                          </span>
                        )}
                        {/* SOV link affordance — only shown when the
                            task actually has at least one SOV link, so
                            unlinked rows don't get a button that looks
                            like it does nothing useful. Click opens the
                            properties modal where the link can be
                            edited / removed via <TaskSOVLinks>. To add
                            a FIRST link, the user double-clicks the row
                            (no icon shortcut for that path -- the empty
                            state is the unlinked row itself).
                            stopPropagation so the click doesn't also
                            fire the row's onDoubleClick. */}
                        {hasAllocations && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
                            title={`${taskAllocations[task.id].length} SOV link${taskAllocations[task.id].length === 1 ? '' : 's'} — click to edit`}
                            style={{
                              background: 'none', border: 'none', padding: 0,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', borderRadius: 4,
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                            </svg>
                          </button>
                        )}
                        </span>
                        {task._kids && task.split_pending ? (
                          <button type="button" onClick={(e) => { e.stopPropagation(); setLowWarn({ ids: [task.id], mode: 'moment' }); }} onDoubleClick={(e) => e.stopPropagation()}
                            title={`It still holds ${[task.split_pending.ours_cents ? `$${(task.split_pending.ours_cents / 100).toLocaleString('en-US')} of our SOV` : '', task.split_pending.subs_cents ? `$${(task.split_pending.subs_cents / 100).toLocaleString('en-US')} of its sub's billing` : ''].filter(Boolean).join(' and ')}. Its subtasks should.`}
                            style={{ fontSize: 12, fontWeight: 600, padding: '1px 8px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                              color: 'var(--warning)', background: 'var(--warning-soft)', border: '1px solid color-mix(in oklch, var(--warning) 40%, transparent)' }}>
                            Split to its subtasks
                          </button>
                        ) : null}
                        {(subMove || subProg || sharedSchedule?.technicians?.[task.id]) && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 0 }}>
                        {subMove && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); sharedSchedule.openMove(subMove.id); }}
                            title={`${subMove.sub || 'The sub'} moved it to ${fmtRange(subMove.start, subMove.finish)}. Billing follows Summit's dates until you accept.`}
                            style={{
                              flex: '0 0 auto', fontSize: '11.5px', fontWeight: 600, lineHeight: '18px', padding: '0 7px', borderRadius: 999,
                              color: 'var(--warning)', background: 'var(--warning-soft)', cursor: 'pointer', fontFamily: 'inherit',
                              border: '1px solid color-mix(in oklch, var(--warning) 35%, transparent)', whiteSpace: 'nowrap',
                            }}
                          >Sub moved it</button>
                        )}
                        {subProg && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); sharedSchedule.openTaskProgress(task.id); }}
                            title={saidOf(subProg)}
                            style={{
                              flex: '0 0 auto', fontSize: '11.5px', fontWeight: 600, lineHeight: '18px', padding: '0 7px', borderRadius: 999,
                              color: 'var(--warning)', background: 'var(--warning-soft)', cursor: 'pointer', fontFamily: 'inherit',
                              border: '1px solid color-mix(in oklch, var(--warning) 35%, transparent)', whiteSpace: 'nowrap',
                            }}
                          >Sub says {subProg.sub_pct}%</button>
                        )}
                        {/* Who the sub put on it, on E3 Subs (2026-10-24): initials, the names in an instant pop-up on hover (10-28) */}
                        {sharedSchedule?.technicians?.[task.id] && (
                          <span {...techTipOn(techTipOf(task, sharedSchedule.technicians[task.id]))}
                            aria-label={`Technicians: ${sharedSchedule.technicians[task.id].map((x) => x.name).join(', ')}`}
                            style={{ flex: '0 0 auto', display: 'inline-flex', gap: '3px', cursor: 'default' }}>
                            {sharedSchedule.technicians[task.id].slice(0, 3).map((x) => (
                              <span key={x.name} style={{
                                width: '20px', height: '20px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '10px', fontWeight: 600, background: 'var(--accent-soft)', color: 'var(--accent-text)',
                              }}>{x.name.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}</span>
                            ))}
                            {sharedSchedule.technicians[task.id].length > 3 && (
                              <span style={{ fontSize: '11px', color: 'var(--text-3)', alignSelf: 'center' }}>+{sharedSchedule.technicians[task.id].length - 3}</span>
                            )}
                          </span>
                        )}
                          </span>
                        )}
                      </div>

                      {/* Type — redesigned as a 7px leading dot in the type's
                          accent color (was a full pastel pill). Tooltip carries
                          the type name so we don't lose information. */}
                      <div title={task.task_type} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <span
                          aria-hidden
                          style={{
                            width: 7, height: 7, borderRadius: 999,
                            // a milestone's own color, when it has one (lib/milestoneColor.js)
                            background: milestoneColor(task, getTaskTypeColorVar(task.task_type)),
                            boxShadow: `0 0 0 2px color-mix(in oklch, ${milestoneColor(task, getTaskTypeColorVar(task.task_type))} 15%, transparent)`,
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ fontSize: 12, color: 'var(--text-4)', letterSpacing: '0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {task.task_type}
                        </span>
                      </div>

                      {/* Dates + duration — two-line stack per Variant B
                          spec (date range on top, duration below). */}
                      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                        <div className="mono" style={{ fontSize: 12.5, color: 'var(--text-2)', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
                          <span>{fmtCompactDate(task.start_date)}</span>
                          <span style={{ color: 'var(--text-4)', margin: '0 4px' }}>{'→'}</span>
                          <span>{fmtCompactDate(task.finish_date)}</span>
                        </div>
                        <div className="mono" title={String(workWeek) === '7' ? 'Calendar days (7-day week)' : 'Working days (Mon–Fri)'} style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, letterSpacing: '-0.005em' }}>
                          {duration} day{duration === 1 ? '' : 's'}
                        </div>
                      </div>

                      {/* % Done with progress bar */}
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          if (task._kids) return; // its % comes from its subtasks
                          handlePercentCompleteDoubleClick(task.id, pct);
                        }}
                      >
                        {editingPercentCompleteId === task.id ? (
                          <input
                            type="number" min="0" max="100"
                            value={editingPercentCompleteValue}
                            onChange={(e) => setEditingPercentCompleteValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handlePercentCompleteEditSave(task.id);
                              else if (e.key === 'Escape') handlePercentCompleteEditCancel();
                            }}
                            onBlur={() => handlePercentCompleteEditSave(task.id)}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: '50px', fontSize: '13.5px', padding: '2px 4px', border: '1px solid #3b82f6', borderRadius: '4px', outline: 'none' }}
                          />
                        ) : (
                          <>
                            <div style={{
                              width: '50px', height: '4px',
                              background: 'var(--border-subtle)',
                              borderRadius: 999, overflow: 'hidden',
                            }}>
                              <div style={{
                                width: `${Math.min(pct, 100)}%`, height: '100%',
                                background: pct >= 100 ? 'var(--success)' : 'var(--accent)',
                                opacity: pct === 0 ? 0.3 : 1,
                                borderRadius: 999, transition: 'width 0.3s',
                              }} />
                            </div>
                            <span className="mono" style={{ fontSize: '12.5px', color: pct === 0 ? 'var(--text-4)' : 'var(--text-2)', fontWeight: 500 }}>
                              {pct}%
                            </span>
                          </>
                        )}
                      </div>

                    </div>

                    {/* SOV Link modal rendered at bottom of component */}
                  </React.Fragment>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {lowWarn && <SplitDown queue={lowWarn.ids} mode={lowWarn.mode} onChanged={() => fetchTasks()} onClose={() => { const a = lowWarn.after; setLowWarn(null); fetchTasks(); if (a) a(); }} />}

      {/* Add Section Modal */}
      {showAddSectionModal && (
        <div className="modal-overlay" onClick={() => setShowAddSectionModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: t.bg, borderRadius: '12px', width: '420px', maxWidth: '90vw',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'slideUp 0.2s ease',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '18px 24px', borderBottom: `1px solid ${t.cardBorder}`,
            }}>
              <h3 style={{ margin: 0, fontSize: '17.5px', fontWeight: 600 }}>Add New Section</h3>
              <button
                onClick={() => setShowAddSectionModal(false)}
                style={{
                  background: 'none', border: 'none', fontSize: '21.5px', color: t.textMuted,
                  cursor: 'pointer', padding: '0 4px', lineHeight: 1,
                }}
              >&times;</button>
            </div>
            {/* Body */}
            <div style={{ padding: '24px' }}>
              <label style={{ display: 'block', fontSize: '14.5px', fontWeight: 600, color: t.text, marginBottom: '6px' }}>
                Section Name (Building Area / Phase)
              </label>
              <input
                type="text"
                autoFocus
                value={newSectionName}
                onChange={(e) => { setNewSectionName(e.target.value); setSectionNameError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirmAddSection(); } }}
                placeholder="e.g. Elementary School, High School Wing B"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: '15.5px',
                  border: sectionNameError ? '1px solid #ef4444' : `1px solid ${t.cardBorder}`,
                  borderRadius: '8px', outline: 'none', boxSizing: 'border-box',
                  transition: 'border-color 0.15s',
                }}
                onFocus={(e) => { if (!sectionNameError) e.target.style.borderColor = '#3b82f6'; }}
                onBlur={(e) => { if (!sectionNameError) e.target.style.borderColor = t.cardBorder; }}
              />
              {sectionNameError && (
                <div style={{ color: '#ef4444', fontSize: '13.5px', marginTop: '6px' }}>{sectionNameError}</div>
              )}
            </div>
            {/* Footer */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: '10px',
              padding: '16px 24px', borderTop: '1px solid var(--border-subtle)',
            }}>
              <button
                onClick={() => setShowAddSectionModal(false)}
                disabled={creatingSection}
                style={{
                  padding: '8px 20px', fontSize: '14.5px', fontWeight: 500,
                  border: `1px solid ${t.cardBorder}`, borderRadius: '8px', background: t.bg,
                  color: t.text,
                  cursor: creatingSection ? 'not-allowed' : 'pointer',
                  opacity: creatingSection ? 0.6 : 1,
                }}
              >Cancel</button>
              <button
                onClick={handleConfirmAddSection}
                disabled={creatingSection}
                style={{
                  padding: '8px 20px', fontSize: '14.5px', fontWeight: 600,
                  border: 'none', borderRadius: '8px', background: '#3b82f6',
                  color: '#fff',
                  cursor: creatingSection ? 'wait' : 'pointer',
                  opacity: creatingSection ? 0.8 : 1,
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}
              >
                {creatingSection && (
                  <span style={{
                    width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: '#fff', borderRadius: '50%',
                    animation: 'spin 0.7s linear infinite', display: 'inline-block',
                  }} />
                )}
                {creatingSection ? 'Creating…' : 'Create Section'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task import — themed LoadingDialog. Same shared primitive as
          SOVTab. Stays mounted across importing → success / error so
          the bar animates smoothly. */}
      <LoadingDialog
        open={!!importProgress}
        state={
          importProgress?.status === 'success' ? 'complete'
            : importProgress?.status === 'error' ? 'error'
            : 'progress'
        }
        noun="tasks"
        current={importProgress?.count || 0}
        total={importProgress?.total || 0}
        errorMessage={importProgress?.error}
        cancellable={false}
        onDismiss={() => setImportProgress(null)}
        onRetry={() => setImportProgress(null)}
      />

      {/* Add Task Modal */}
      {showModal && (() => {
        // Warm-cream + sage tokens, matching the Task Properties (TaskEditModal)
        // design so Add + Edit feel like one component.
        const atInputStyle = {
          width: '100%', height: 34, padding: '0 10px', background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 14.5,
          color: 'var(--text-1)', letterSpacing: '-0.005em', outline: 'none',
          boxSizing: 'border-box', fontFamily: 'inherit',
        };
        const atTextareaStyle = { ...atInputStyle, height: 'auto', minHeight: 56, padding: '8px 10px', resize: 'vertical', lineHeight: 1.45 };
        const atLabelStyle = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };
        const atFieldGroup = { marginBottom: 12 };
        const handleFocus = (e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 2px var(--accent-ring)'; };
        const handleBlur = (e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.boxShadow = 'none'; };
        const errText = (msg) => <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 4 }}>{msg}</div>;
        const closeModal = () => {
          setShowModal(false); setFormErrors({}); setPendingPredecessors([]); setPendingSovLinks([]);
          // Reset the form too, so a cancelled draft (incl. a Status=Complete
          // pick) can't silently pre-fill the next "+ Task" open.
          setFormData({
            task_code: '', task_name: '', task_description: '', building_area_phase: '',
            task_type: 'Labor', start_date: '', finish_date: '', duration_days: 0, duration: '1',
            status: 'Not Started', percent_complete: 0,
            responsible_party: '', client_visible: true, milestone_flag: false, notes: '',
          });
        };
        const sectionHead = (title) => <SectionHead title={title} />; // a card's header (SectionCards, Duncan 2026-10-30)
        const TYPE_COLOR = { Labor: 'var(--type-labor)', Material: 'var(--type-material)', Procurement: 'var(--type-procurement)', Engineering: 'var(--type-engineering)', Inspection: 'var(--type-inspection)', Documents: 'var(--type-documents)', Milestone: 'var(--type-milestone)' };
        const STATUS_META = {
          'Not Started': { color: 'var(--text-3)', bg: 'var(--bg-surface-2)' },
          'In Progress': { color: 'var(--accent-text)', bg: 'var(--accent-soft)' },
          'Complete': { color: 'var(--success)', bg: 'var(--success-soft)' },
        };
        const pill = (color, bg, label) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 999, fontSize: 12.5, fontWeight: 500, color, background: bg, border: `1px solid color-mix(in oklch, ${color} 22%, transparent)`, whiteSpace: 'nowrap' }}>
            <span style={{ width: 5, height: 5, borderRadius: 99, background: color }} />{label}
          </span>
        );
        const typeColor = TYPE_COLOR[formData.task_type] || 'var(--text-3)';
        const stMeta = STATUS_META[formData.status] || STATUS_META['Not Started'];
        return (
          <div onClick={closeModal} style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'oklch(0.3 0.02 90 / 0.42)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 'max(28px, 5vh)',
          }}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 580, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 56px)',
                display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)',
                borderRadius: 16, border: '1px solid var(--border-subtle)',
                boxShadow: '0 20px 50px -10px oklch(0.2 0.02 90 / 0.25), 0 8px 20px oklch(0.2 0.02 90 / 0.08)',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div style={{ padding: '18px 24px 16px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  {pill(typeColor, `color-mix(in oklch, ${typeColor} 12%, transparent)`, formData.task_type || 'Labor')}
                  {pill(stMeta.color, stMeta.bg, formData.status || 'Not Started')}
                  <div style={{ flex: 1 }} />
                  <button type="button" onClick={closeModal} aria-label="Close" style={{
                    width: 30, height: 30, borderRadius: 7, background: 'transparent', border: 'none',
                    color: 'var(--text-3)', fontSize: 22, lineHeight: 1, cursor: 'pointer',
                  }}>&times;</button>
                </div>
                {!addParent ? (
                  <h2 style={{ margin: 0, fontSize: 19.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.018em' }}>Add New Task</h2>
                ) : (
                  <>
                    <h2 style={{ margin: 0, fontSize: 19.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.018em', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 6h11" /><path d="M12 12h8" /><path d="M15 18h5" /><path d="M5 6v4a2 2 0 0 0 2 2h2" /><path d="M10 12v4a2 2 0 0 0 2 2h1" /></svg>
                      Add a subtask
                    </h2>
                    <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>{addParent.building_area_phase} › <span className="mono">{addParent.task_code}</span> {addParent.task_name} › new subtask</div>
                  </>
                )}
              </div>
              {/* Body */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '14px 24px 8px' }}>
                <SectionCards>
                {sectionHead('General', true)}
                {/* Task Name */}
                <div style={atFieldGroup}>
                  <label style={atLabelStyle}>Task Name <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input type="text" name="task_name" value={formData.task_name} onChange={handleFormChange}
                    style={{ ...atInputStyle, borderColor: formErrors.task_name ? 'var(--danger)' : 'var(--border-default)' }}
                    onFocus={handleFocus} onBlur={handleBlur}
                    placeholder="Enter task name" />
                  {formErrors.task_name && errText(formErrors.task_name)}
                </div>
                {/* Description */}
                <div style={atFieldGroup}>
                  <label style={atLabelStyle}>Description</label>
                  <textarea name="task_description" value={formData.task_description} onChange={handleFormChange}
                    rows="2" placeholder="Describe the scope of this task…"
                    style={atTextareaStyle}
                    onFocus={handleFocus} onBlur={handleBlur} />
                </div>
                {/* Section + Type row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, ...atFieldGroup }}>
                  <div>
                    <label style={atLabelStyle}>Section <span style={{ color: 'var(--danger)' }}>*</span></label>
                    {formData._addingNewSection ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          type="text"
                          placeholder="New section name..."
                          value={formData._newSectionName || ''}
                          autoFocus
                          onChange={e => setFormData(f => ({ ...f, _newSectionName: e.target.value }))}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const name = (formData._newSectionName || '').trim();
                              if (name) {
                                createScheduleSection({ project_id: projectId, section_name: name })
                                  .then(() => fetchSections())
                                  .catch(() => {});
                                setFormData(f => ({ ...f, building_area_phase: name, _addingNewSection: false, _newSectionName: '' }));
                              }
                            } else if (e.key === 'Escape') {
                              setFormData(f => ({ ...f, _addingNewSection: false, _newSectionName: '' }));
                            }
                          }}
                          style={{ ...atInputStyle, flex: 1, borderColor: formErrors.building_area_phase ? 'var(--danger)' : 'var(--border-default)' }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const name = (formData._newSectionName || '').trim();
                            if (name) {
                              createScheduleSection({ project_id: projectId, section_name: name })
                                .then(() => fetchSections())
                                .catch(() => {});
                              setFormData(f => ({ ...f, building_area_phase: name, _addingNewSection: false, _newSectionName: '' }));
                            }
                          }}
                          style={{
                            padding: '0 12px', height: 34, fontSize: 13.5, fontWeight: 500, cursor: 'pointer',
                            border: '1px solid color-mix(in oklch, var(--accent) 50%, black)', borderRadius: 8,
                            background: 'var(--accent)', color: '#fff', whiteSpace: 'nowrap',
                          }}
                        >Add</button>
                        <button
                          type="button"
                          onClick={() => setFormData(f => ({ ...f, _addingNewSection: false, _newSectionName: '' }))}
                          style={{
                            padding: '0 10px', height: 34, fontSize: 13.5, cursor: 'pointer',
                            border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--bg-surface)', color: 'var(--text-3)',
                          }}
                        >Cancel</button>
                      </div>
                    ) : (
                      <select name="building_area_phase" value={formData.building_area_phase} onChange={e => {
                          if (e.target.value === '__new__') {
                            setFormData(f => ({ ...f, _addingNewSection: true, _newSectionName: '' }));
                          } else {
                            handleFormChange(e);
                          }
                        }}
                        style={{ ...atInputStyle, cursor: 'pointer', borderColor: formErrors.building_area_phase ? 'var(--danger)' : 'var(--border-default)' }}
                        onFocus={handleFocus} onBlur={handleBlur}>
                        <option value="">— Select Section —</option>
                        {getExistingSections().sort().map(sec => (
                          <option key={sec} value={sec}>{sec}</option>
                        ))}
                        {formData.building_area_phase && !getExistingSections().includes(formData.building_area_phase) && (
                          <option value={formData.building_area_phase}>{formData.building_area_phase} (new section)</option>
                        )}
                        <option value="__new__" style={{ fontWeight: 600 }}>+ Add New Section</option>
                      </select>
                    )}
                    {formErrors.building_area_phase && errText(formErrors.building_area_phase)}
                  </div>
                  <div>
                    <label style={atLabelStyle}>Task Type</label>
                    <select name="task_type" value={formData.task_type} onChange={handleFormChange}
                      style={{ ...atInputStyle, cursor: 'pointer' }} onFocus={handleFocus} onBlur={handleBlur}>
                      <option>Labor</option>
                      <option>Material</option>
                      <option>Procurement</option>
                      <option>Engineering</option>
                      <option>Inspection</option>
                      <option>Documents</option>
                      <option>Milestone</option>
                    </select>
                  </div>
                </div>
                {/* Client Visible + Milestone, up from Options (Duncan 2026-11-02), with the milestone's color under them */}
                <div style={{ display: 'flex', gap: 28, ...atFieldGroup }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14.5, color: 'var(--text-1)', cursor: 'pointer' }}>
                    <input type="checkbox" name="client_visible" checked={formData.client_visible} onChange={handleFormChange}
                      style={{ width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                    Client Visible
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14.5, color: 'var(--text-1)', cursor: 'pointer' }}>
                    <input type="checkbox" name="milestone_flag" checked={formData.milestone_flag} onChange={handleFormChange}
                      style={{ width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                    Milestone
                  </label>
                </div>
                {!!formData.milestone_flag && (
                  <MilestoneColorPicker value={formData.milestone_color} onChange={(c) => setFormData((f) => ({ ...f, milestone_color: c }))} />
                )}
                {/* Subtask of (2026-10-29): one of this section's tasks, one level deep; then where it goes, and the two arrow
                    checks (SubtaskPlacement.js) */}
                {formData.building_area_phase && (() => {
                  const opts = tasks.filter((x) => (x.building_area_phase || '') === formData.building_area_phase && x.parent_task_id == null && !Number(x.milestone_flag));
                  const val = addParent ? String(addParent.id) : '';
                  return (
                    <div style={{ ...atFieldGroup }}>
                      <label style={atLabelStyle}>Subtask of</label>
                      <select name="parent_task_id" value={val} onChange={(e) => { setArrowPicks({}); setFormData((f) => ({ ...f, parent_task_id: e.target.value ? Number(e.target.value) : null })); }}
                        style={{ ...atInputStyle, cursor: 'pointer' }} onFocus={handleFocus} onBlur={handleBlur}>
                        <option value="">None: it's a task</option>
                        {opts.map((x) => <option key={x.id} value={String(x.id)}>{x.task_code} · {x.task_name}</option>)}
                      </select>
                      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>A subtask's dates and % roll up into its task. Its SOV links and its work are its own.</div>
                      {addParent && (() => {
                        const me = { id: '__new', task_name: formData.task_name, start_date: (addPreview && addPreview.start_date) || formData.start_date, finish_date: (addPreview && addPreview.finish_date) || formData.finish_date || formData.start_date };
                        const sibs = tasks.filter((x) => Number(x.parent_task_id) === addParent.id);
                        return <SubtaskPlacement parent={addParent} siblings={sibs} me={me}
                          offers={arrowOffers({ parent: addParent, siblings: sibs, me, myPreds: pendingPredecessors, allTasks: tasks, isNew: true })} picks={arrowPicks}
                          onPick={(k, v) => setArrowPicks((pk) => ({ ...pk, [k]: v }))}
                          handed={handedArrows({ parent: addParent, siblings: sibs, me, allTasks: tasks })} />;
                      })()}
                    </div>
                  );
                })()}
                {/* Status + Responsible Party row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, ...atFieldGroup }}>
                  <div>
                    <label style={atLabelStyle}>Status</label>
                    <select name="status" value={formData.status || 'Not Started'}
                      onChange={(e) => {
                        const status = e.target.value;
                        let pct = parseFloat(formData.percent_complete) || 0;
                        if (status === 'Complete' && pct < 100) pct = 100;
                        else if (status === 'Not Started' && pct > 0) pct = 0;
                        else if (status === 'In Progress' && (pct === 0 || pct === 100)) pct = 50;
                        setFormData(f => ({ ...f, status, percent_complete: pct }));
                      }}
                      style={{ ...atInputStyle, cursor: 'pointer' }} onFocus={handleFocus} onBlur={handleBlur}>
                      <option>Not Started</option>
                      <option>In Progress</option>
                      <option>Complete</option>
                    </select>
                  </div>
                  <div>
                    <label style={atLabelStyle}>Responsible Party</label>
                    <ResponsiblePartyField
                      value={formData.responsible_party} subcontractorId={formData.subcontractor_id}
                      onChange={(patch) => setFormData((f) => ({ ...f, ...patch }))}
                      inputStyle={atInputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                  </div>
                </div>

                {/* SCHEDULE — #51: pick predecessors below and type a duration; the start comes
                    from them (a start typed here pins it) and the finish is calculated. */}
                {sectionHead('Schedule')}
                {(() => {
                  const fmt = (v) => {
                    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
                    return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                  };
                  const driverName = addPreview && addPreview.driver && typeof addPreview.driver === 'object' ? addPreview.driver.task_name : null;
                  const typed = formData.start_date;
                  const linkWins = typed && pendingPredecessors.length > 0 && addPreview && addPreview.start_date > typed;
                  const caption = typed
                    ? (linkWins ? `Can't start before ${fmt(addPreview.start_date)}${driverName ? ` — ${driverName} comes first` : ''}`
                      : `Pinned: starts no earlier than ${fmt(typed)}`)
                    : driverName ? `From ${driverName}`
                      : addPreview && addPreview.start_date ? (addParent ? `Starts with ${addParent.task_name}` : "Starts on the project's start date") : '';
                  const milestone = !!formData.milestone_flag;
                  // The start the calendar counts from: one typed or picked here, else where the links or the project put it.
                  const fromStart = typed || (addPreview && addPreview.start_date) || '';
                  const addCalendar = (mode) => (
                    <DateRangePicker
                      key={mode} mode={mode} start={fromStart} finish={addPreview && addPreview.finish_date} workWeek={String(workWeek) === '7' ? '7' : '5'}
                      anchorRef={mode === 'start' ? addStartRef : addFinishRef}
                      onStart={(iso) => handleFormChange({ target: { name: 'start_date', value: iso, type: 'date' } })}
                      onFinish={(iso, days) => handleFormChange({ target: { name: 'duration', value: String(Math.max(1, days)), type: 'text' } })}
                      onClose={() => setAddPicker(null)}
                      align={mode === 'start' ? 'left' : 'right'}
                    />
                  );
                  return (
                    <div style={atFieldGroup}>
                      {/* Start · Duration · Finish; on a phone Finish drops to its own line. Start and Finish open one
                          calendar (DateRangePicker), as in the task window. */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        <div style={{ flex: '1 1 150px', minWidth: 0 }}>
                          <label style={atLabelStyle}>Start</label>
                          <div style={{ position: 'relative' }}>
                            <input ref={addStartRef} type="date" name="start_date" className="tsk-date" value={formData.start_date} onChange={handleFormChange}
                              onClick={() => { if (!milestone) setAddPicker('start'); }}
                              style={{ ...atInputStyle, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em', cursor: 'pointer', borderColor: formErrors.start_date ? 'var(--danger)' : 'var(--border-default)' }}
                              onFocus={handleFocus} onBlur={handleBlur} />
                            {addPicker === 'start' && !milestone && addCalendar('start')}
                          </div>
                          {formErrors.start_date ? errText(formErrors.start_date) : caption && (
                            <div style={{ fontSize: 12.5, marginTop: 5, color: linkWins ? 'var(--warning)' : 'var(--text-3)' }}>{caption}</div>
                          )}
                        </div>
                        <div style={{ flex: '0 0 110px' }}>
                          <label style={atLabelStyle}>Duration <span style={{ color: 'var(--danger)' }}>*</span></label>
                          <div style={{ position: 'relative' }}>
                            <input type="text" inputMode="numeric" name="duration"
                              value={milestone ? '1' : formData.duration}
                              onChange={(e) => handleFormChange({ target: { name: 'duration', value: e.target.value.replace(/[^0-9]/g, ''), type: 'text' } })}
                              disabled={milestone}
                              style={{ ...atInputStyle, paddingRight: 40, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em', borderColor: formErrors.duration ? 'var(--danger)' : 'var(--border-default)' }}
                              onFocus={handleFocus} onBlur={handleBlur} />
                            <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12.5, color: 'var(--text-3)', pointerEvents: 'none' }}>days</span>
                          </div>
                          {formErrors.duration && errText(formErrors.duration)}
                        </div>
                        <div style={{ flex: '1 1 150px', minWidth: 0 }}>
                          <label style={atLabelStyle}>Finish</label>
                          {milestone ? (
                            <div style={{ ...atInputStyle, display: 'flex', alignItems: 'center', background: 'var(--bg-surface-2)', border: '1px dashed var(--border-default)', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em' }}>
                              {(addPreview && fmt(addPreview.finish_date)) || '—'}
                            </div>
                          ) : (
                            <div style={{ position: 'relative' }}>
                              {/* No start yet: the calendar asks for one first. */}
                              <button ref={addFinishRef} type="button" aria-haspopup="dialog" aria-expanded={addPicker === 'finish'}
                                onClick={() => setAddPicker((p) => (p ? null : fromStart ? 'finish' : 'start'))}
                                style={{ ...atInputStyle, display: 'flex', alignItems: 'center', gap: 6, textAlign: 'left', cursor: 'pointer', fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em',
                                  borderColor: addPicker === 'finish' ? 'var(--accent)' : 'var(--border-default)' }}>
                                <span style={{ flex: 1 }}>{(addPreview && fmt(addPreview.finish_date)) || '—'}</span>
                                <Icon name="calendar" size={13} />
                              </button>
                              {addPicker === 'finish' && addCalendar('finish')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 6 }}>
                        {milestone ? 'Milestone: one day' : String(workWeek) === '7' ? 'Calendar days, 7-day week' : 'Working days, Mon–Fri'}
                      </div>
                    </div>
                  );
                })()}

                {/* PREDECESSORS */}
                {sectionHead('Predecessors')}
                <div style={atFieldGroup}>
                  <PredecessorSection
                    currentTask={null}
                    tasks={tasks}
                    editingTaskId={null}
                    inputStyle={atInputStyle}
                    fieldGroup={{ marginBottom: 0 }}
                    mode="local"
                    localPredecessors={pendingPredecessors}
                    onLocalChange={setPendingPredecessors}
                    excludeIds={addParent ? [addParent.id] : []}
                  />
                </div>

                {/* SOV LINKS */}
                {sectionHead('SOV Links')}
                <div style={atFieldGroup}>
                  <TaskSOVLinks
                    mode="local"
                    projectId={projectId}
                    localLinks={pendingSovLinks}
                    onLocalChange={setPendingSovLinks}
                  />
                </div>

                {/* NOTES */}
                {sectionHead('Notes')}
                <textarea name="notes" value={formData.notes} onChange={handleFormChange}
                  rows="2" placeholder="Add notes for the field team or PM…"
                  style={atTextareaStyle}
                  onFocus={handleFocus} onBlur={handleBlur} />
                <div style={{ height: 8 }} />
                </SectionCards>
              </div>
              {/* Footer */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)', flexShrink: 0,
              }}>
                <div style={{ flex: 1 }} />
                <button type="button"
                  onClick={closeModal}
                  disabled={creatingTask}
                  style={{
                    padding: '0 14px', height: 36, borderRadius: 8, fontSize: 14.5, fontWeight: 500,
                    color: 'var(--text-2)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
                    cursor: creatingTask ? 'not-allowed' : 'pointer', opacity: creatingTask ? 0.6 : 1,
                  }}
                >Cancel</button>
                <button type="button"
                  onClick={() => handleAddTask()}
                  disabled={creatingTask}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '0 16px', height: 36, borderRadius: 8, fontSize: 14.5, fontWeight: 500, color: '#fff',
                    background: 'var(--accent)', border: '1px solid color-mix(in oklch, var(--accent) 50%, black)',
                    boxShadow: '0 1px 0 color-mix(in oklch, var(--accent) 35%, black), 0 1px 3px oklch(0.3 0.05 135 / 0.18)',
                    cursor: creatingTask ? 'wait' : 'pointer', opacity: creatingTask ? 0.8 : 1,
                  }}
                >
                  {creatingTask && (
                    <span style={{
                      width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff', borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite', display: 'inline-block',
                    }} />
                  )}
                  {creatingTask ? 'Creating…' : 'Create Task'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Task Properties Panel */}
      {editingTaskId && (() => {
        const currentTask = tasks.find(t => t.id === editingTaskId);
        const taskAllocs = taskAllocations[editingTaskId] || [];
        const pct = currentTask?.percent_complete || 0;

        // Shared input style
        const inputStyle = {
          width: '100%', padding: '8px 12px', fontSize: '14.5px',
          border: `1px solid ${t.cardBorder}`, borderRadius: '8px', outline: 'none',
          transition: 'border-color 0.2s', backgroundColor: t.bg,
          boxSizing: 'border-box',
        };
        const inputDisabledStyle = { ...inputStyle, backgroundColor: t.bgAlt, color: t.textSecondary, cursor: 'default' };
        const labelStyle = { display: 'block', fontSize: '12.5px', fontWeight: 600, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' };
        const sectionTitleStyle = { fontSize: '13.5px', fontWeight: 700, color: t.text, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid var(--border-subtle)' };
        const fieldGroup = { marginBottom: '14px' };

        return (
          <div className="modal-overlay" onClick={() => setEditingTaskId(null)}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: t.bg, borderRadius: '16px', width: '580px', maxHeight: '88vh',
                overflow: 'hidden', display: 'flex', flexDirection: 'column',
                boxShadow: '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
                animation: 'slideUp 0.25s ease',
              }}
            >
              {/* Header */}
              <div style={{
                padding: '20px 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                borderBottom: '1px solid var(--border-subtle)',
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <span style={{
                      ...getTaskTypeStyle(editFormData.task_type),
                      padding: '3px 10px', borderRadius: '12px', fontSize: '12.5px', fontWeight: 600,
                    }}>
                      {editFormData.task_type}
                    </span>
                    <span style={{ fontSize: '13.5px', color: t.textMuted, fontWeight: 500 }}>{editFormData.task_code}</span>
                    <span style={{
                      ...getStatusStyle(editFormData.status),
                      padding: '3px 10px', borderRadius: '12px', fontSize: '12.5px', fontWeight: 500,
                    }}>
                      {editFormData.status || 'Not Started'}
                    </span>
                  </div>
                  <h2 style={{ margin: 0, fontSize: '19.5px', fontWeight: 600, color: t.heading }}>
                    {editFormData.task_name || 'Task Properties'}
                  </h2>
                </div>
                <button
                  onClick={() => setEditingTaskId(null)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    color: t.textMuted, fontSize: '21.5px', lineHeight: 1, borderRadius: '6px',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.target.style.backgroundColor = t.dark ? 'rgba(255,255,255,0.1)' : '#f1f5f9'; e.target.style.color = t.heading; }}
                  onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = t.textMuted; }}
                >
                  &times;
                </button>
              </div>

              {/* Scrollable body */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

                {/* General section */}
                <div style={sectionTitleStyle}>General</div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Task Name</label>
                  <input type="text" name="task_name" value={editFormData.task_name} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Description</label>
                  <textarea name="task_description" value={editFormData.task_description} onChange={handleEditFormChange} rows="2" style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', ...fieldGroup }}>
                  <div>
                    <label style={labelStyle}>Building Area / Phase</label>
                    <input type="text" name="building_area_phase" value={editFormData.building_area_phase} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                  </div>
                  <div>
                    <label style={labelStyle}>Task Type</label>
                    <select name="task_type" value={editFormData.task_type} onChange={handleEditFormChange} style={inputStyle}>
                      <option>Labor</option>
                      <option>Material</option>
                      <option>Procurement</option>
                      <option>Engineering</option>
                      <option>Inspection</option>
                      <option>Documents</option>
                      <option>Milestone</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', ...fieldGroup }}>
                  <div>
                    <label style={labelStyle}>Status</label>
                    <select name="status" value={editFormData.status || 'Not Started'} onChange={handleEditFormChange} style={inputStyle}>
                      <option>Not Started</option>
                      <option>In Progress</option>
                      <option>Complete</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Responsible Party</label>
                    <ResponsiblePartyField
                      value={editFormData.responsible_party} subcontractorId={editFormData.subcontractor_id}
                      onChange={(patch) => setEditFormData((f) => ({ ...f, ...patch }))}
                      inputStyle={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                  </div>
                </div>

                {/* Schedule section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Schedule</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: '12px', ...fieldGroup }}>
                  <div>
                    <label style={labelStyle}>Start Date</label>
                    <input type="date" name="start_date" value={editFormData.start_date} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                  </div>
                  <div>
                    <label style={labelStyle}>Finish Date</label>
                    <input type="date" name="finish_date" value={editFormData.finish_date} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                  </div>
                  <div>
                    <label style={labelStyle}>Duration</label>
                    <input type="text" value={`${editDuration} days`} disabled style={inputDisabledStyle} />
                  </div>
                </div>

                {/* Progress slider */}
                {(() => {
                  const maxPct = getMaxProgress(currentTask);
                  // Round max down to nearest step of 5 so the slider aligns to step increments
                  const sliderMax = Math.floor(maxPct / 5) * 5;
                  return (
                    <div style={{ ...fieldGroup }}>
                      <label style={labelStyle}>
                        Progress — {pct}%
                        {maxPct < 100 && <span style={{ color: '#f59e0b', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> (max {sliderMax}% — {latestDraftPeriodEnd ? `schedule prorated to billing period end (${latestDraftPeriodEnd})` : 'schedule prorated to today'})</span>}
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                          <input
                            type="range"
                            min="0"
                            max={sliderMax}
                            step="5"
                            value={Math.min(pct, sliderMax)}
                            onChange={(e) => {
                              const newPct = Math.min(parseInt(e.target.value, 10), sliderMax);
                              handlePercentCompleteChange(editingTaskId, newPct);
                            }}
                            style={{
                              width: '100%', height: '8px', cursor: sliderMax > 0 ? 'pointer' : 'not-allowed',
                              accentColor: pct >= 100 ? '#22c55e' : pct >= 50 ? '#3b82f6' : '#94a3b8',
                            }}
                            disabled={sliderMax === 0}
                          />
                          {maxPct < 100 && (
                            <div style={{
                              position: 'absolute', top: '-2px', height: '12px', right: 0,
                              width: `${100 - maxPct}%`, background: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px)',
                              borderRadius: '0 4px 4px 0', pointerEvents: 'none',
                            }} />
                          )}
                        </div>
                        <span style={{ fontSize: '14.5px', fontWeight: 600, color: t.text, minWidth: '40px', textAlign: 'right' }}>{pct}%</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Predecessors section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Predecessors</div>
                <PredecessorSection
                  currentTask={currentTask}
                  tasks={tasks}
                  editingTaskId={editingTaskId}
                  inputStyle={inputStyle}
                  fieldGroup={fieldGroup}
                  addPredecessor={addPredecessor}
                  updatePredecessor={updatePredecessor}
                  removePredecessor={removePredecessor}
                  fetchTasks={fetchTasks}
                  confirm={confirm}
                />

                {/* Dependent Tasks (successors) section */}
                {(() => {
                  const successors = tasks.filter(t =>
                    (t.predecessors || []).some(p => p.predecessor_task_id != null && Number(p.predecessor_task_id) === Number(editingTaskId))
                  );
                  return (
                    <div>
                      <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Dependent Tasks</div>
                      {successors.length === 0 ? (
                        <div style={{ fontSize: '14.5px', color: t.textMuted, padding: '8px 0' }}>
                          No tasks depend on this task
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '4px 0' }}>
                          {successors.map(s => {
                            const pred = (s.predecessors || []).find(p => Number(p.predecessor_task_id) === Number(editingTaskId));
                            const depLabel = DEP_TYPES.find(d => d.value === pred?.dependency_type)?.short || pred?.dependency_type || 'FS';
                            const lagText = pred?.lag_days ? ` +${pred.lag_days}d` : '';
                            return (
                              <div key={s.id} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 10px', backgroundColor: t.bgAlt, borderRadius: '6px',
                                border: `1px solid ${t.cardBorder}`, fontSize: '14.5px',
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                  </svg>
                                  <div style={{ minWidth: 0 }}>
                                    <div>
                                      <span style={{ fontWeight: 500, color: t.text }}>{s.task_code}</span>
                                      <span style={{ color: t.textSecondary, marginLeft: '6px' }}>{s.task_name}</span>
                                    </div>
                                    {(s.start_date || s.finish_date) && (
                                      <div style={{ fontSize: '12.5px', color: t.textMuted, marginTop: '2px' }}>
                                        {s.start_date ? `Start: ${fmtDate(s.start_date)}` : ''}
                                        {s.start_date && s.finish_date ? '  —  ' : ''}
                                        {s.finish_date ? `Finish: ${fmtDate(s.finish_date)}` : ''}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <span style={{
                                  fontSize: '12.5px', fontWeight: 600, color: '#8b5cf6',
                                  backgroundColor: '#f5f3ff', padding: '2px 8px', borderRadius: '10px',
                                }}>
                                  {depLabel}{lagText}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Options section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Options</div>
                <div style={{ display: 'flex', gap: '24px', ...fieldGroup }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14.5px', color: t.text, cursor: 'pointer' }}>
                    <input type="checkbox" name="client_visible" checked={editFormData.client_visible} onChange={handleEditFormChange}
                      style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                    Client Visible
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14.5px', color: t.text, cursor: 'pointer' }}>
                    <input type="checkbox" name="milestone_flag" checked={editFormData.milestone_flag} onChange={handleEditFormChange}
                      style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                    Milestone
                  </label>
                </div>

                {/* SOV Links preview */}
                {taskAllocs.length > 0 && (
                  <>
                    <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>SOV Links</div>
                    <div style={{ ...fieldGroup }}>
                      {taskAllocs.map(a => {
                        const contractAmt = parseFloat(a.sov_contract_amount) || 0;
                        const pct = contractAmt > 0 ? Math.round((parseFloat(a.allocation_amount) / contractAmt) * 100) : 0;
                        return (
                          <div key={a.id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 12px', backgroundColor: t.bgAlt, borderRadius: '8px',
                            marginBottom: '6px', fontSize: '13.5px',
                          }}>
                            <div>
                              <span style={{ fontWeight: 600, color: t.text }}>{a.sov_code}</span>
                              <span style={{ color: t.textSecondary, marginLeft: '8px' }}>{a.sov_line_description}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 600, color: t.text }}>
                                ${(a.allocation_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </span>
                              <span style={{
                                fontSize: '12.5px', fontWeight: 600, color: '#3b82f6',
                                backgroundColor: '#eff6ff', padding: '2px 8px', borderRadius: '10px',
                              }}>
                                {pct}%
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Notes section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Notes</div>
                <div style={fieldGroup}>
                  <textarea name="notes" value={editFormData.notes} onChange={handleEditFormChange} rows="3" style={{ ...inputStyle, resize: 'vertical', minHeight: '70px' }} placeholder="Add notes about this task..." onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                </div>

              </div>
              {/* Footer — pinned outside scrollable area */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '16px 24px', borderTop: '1px solid var(--border-subtle)',
                flexShrink: 0,
              }}>
                <button type="button"
                  onClick={() => { handleDeleteTask(editingTaskId); }}
                  disabled={deletingTaskId === editingTaskId || savingEdit}
                  style={{
                    background: 'none', border: '1px solid #fecaca', color: '#dc2626',
                    padding: '8px 16px', borderRadius: '8px', fontSize: '14.5px', fontWeight: 500,
                    cursor: deletingTaskId === editingTaskId ? 'wait' : (savingEdit ? 'not-allowed' : 'pointer'),
                    opacity: (deletingTaskId === editingTaskId || savingEdit) ? 0.7 : 1,
                    transition: 'all 0.15s',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={(e) => { if (deletingTaskId !== editingTaskId && !savingEdit) e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  {deletingTaskId === editingTaskId && (
                    <span style={{
                      width: 14, height: 14, border: '2px solid rgba(0,0,0,0.1)',
                      borderTopColor: '#dc2626', borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite', display: 'inline-block',
                    }} />
                  )}
                  {deletingTaskId === editingTaskId ? 'Deleting…' : 'Delete'}
                </button>
                <div style={{ flex: 1 }} />
                <button type="button" onClick={() => setEditingTaskId(null)}
                  disabled={savingEdit || deletingTaskId === editingTaskId}
                  style={{
                    background: t.bgAlt, border: 'none', color: t.textSecondary,
                    padding: '8px 20px', borderRadius: '8px', fontSize: '14.5px', fontWeight: 500,
                    cursor: (savingEdit || deletingTaskId === editingTaskId) ? 'not-allowed' : 'pointer',
                    opacity: (savingEdit || deletingTaskId === editingTaskId) ? 0.6 : 1,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { if (!savingEdit && deletingTaskId !== editingTaskId) e.currentTarget.style.backgroundColor = t.dark ? '#2d3748' : '#e2e8f0'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = t.bgAlt; }}
                >
                  Cancel
                </button>
                <button type="button"
                  onClick={() => handleSaveEdit()}
                  disabled={savingEdit || deletingTaskId === editingTaskId}
                  style={{
                    background: '#3b82f6', border: 'none', color: '#fff',
                    padding: '8px 24px', borderRadius: '8px', fontSize: '14.5px', fontWeight: 600,
                    cursor: savingEdit ? 'wait' : (deletingTaskId === editingTaskId ? 'not-allowed' : 'pointer'),
                    opacity: (savingEdit || deletingTaskId === editingTaskId) ? 0.8 : 1,
                    transition: 'all 0.15s',
                    boxShadow: '0 1px 3px rgba(59,130,246,0.3)',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={(e) => { if (!savingEdit && deletingTaskId !== editingTaskId) e.currentTarget.style.backgroundColor = '#2563eb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}
                >
                  {savingEdit && (
                    <span style={{
                      width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff', borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite', display: 'inline-block',
                    }} />
                  )}
                  {savingEdit ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <ConfirmDialog {...confirmDialogProps} />
      {techTip}

      {/* ═══ Schedule Filter popover ═══
          Three grouped checkbox sections (AND-combined): Section,
          Status, Type. Toggles persist into sharedSchedule.scheduleFilter
          so both the Tasks panel AND the Gantt panel re-filter in
          lockstep. Each group has its own Clear link. */}
      <Popover
        open={filterMenuOpen}
        anchor={filterMenuAnchor}
        onClose={() => setFilterMenuOpen(false)}
        minWidth={250}
        align={popoverAlign}
      >
        {(() => {
          const f = sharedSchedule?.scheduleFilter
            || { sections: new Set(), statuses: new Set(), types: new Set() };
          const setF = sharedSchedule?.setScheduleFilter || (() => {});
          const toggleIn = (dim, value) => {
            setF((prev) => {
              const nextDim = new Set(prev[dim]);
              if (nextDim.has(value)) nextDim.delete(value);
              else nextDim.add(value);
              return { ...prev, [dim]: nextDim };
            });
          };
          const clearDim = (dim) => setF((prev) => ({ ...prev, [dim]: new Set() }));

          // Section choices = sections actually present on this
          // project. Pulled from the canonical (unfiltered) sections
          // list so a user can still re-add a section they just
          // filtered out. Drag-and-drop section reordering happens
          // on the same canonical list, so the order here matches.
          const sectionChoices = (sharedSchedule?.sections || sections);

          return (
            <>
              {/* SECTION group */}
              <PopoverHeader
                title="Section"
                onClear={f.sections.size > 0 ? () => clearDim('sections') : null}
              />
              {sectionChoices.length === 0 ? (
                <div style={{ padding: '6px 10px', fontSize: 13.5, color: 'var(--text-4)' }}>
                  No sections defined.
                </div>
              ) : (
                sectionChoices.map((s) => (
                  <CheckMenuItem
                    key={`sec-${s}`}
                    checked={f.sections.has(s)}
                    label={s}
                    onClick={() => toggleIn('sections', s)}
                  />
                ))
              )}

              <PopoverDivider />

              {/* STATUS group */}
              <PopoverHeader
                title="Status"
                onClear={f.statuses.size > 0 ? () => clearDim('statuses') : null}
              />
              {['Not Started', 'In Progress', 'Complete'].map((s) => (
                <CheckMenuItem
                  key={`st-${s}`}
                  checked={f.statuses.has(s)}
                  label={s}
                  onClick={() => toggleIn('statuses', s)}
                />
              ))}

              <PopoverDivider />

              {/* TYPE group */}
              <PopoverHeader
                title="Type"
                onClear={f.types.size > 0 ? () => clearDim('types') : null}
              />
              {SCHEDULE_TASK_TYPES.map((tt) => (
                <CheckMenuItem
                  key={`tp-${tt}`}
                  checked={f.types.has(tt)}
                  label={tt}
                  onClick={() => toggleIn('types', tt)}
                />
              ))}
            </>
          );
        })()}
      </Popover>

      {/* ═══ Schedule Sort popover ═══
          Single-select radio list. Sort key flows up via
          sharedSchedule.setScheduleSort so the Gantt sees the same
          ordering. Within-section only — cross-section order remains
          AIA-grouped via the sections list. */}
      <Popover
        open={sortMenuOpen}
        anchor={sortMenuAnchor}
        onClose={() => setSortMenuOpen(false)}
        minWidth={250}
        align={popoverAlign}
      >
        {(() => {
          const sortKey = sharedSchedule?.scheduleSort || 'default';
          const isCustomSort = sortKey !== 'default';
          return (
            <>
              <PopoverHeader
                title="Sort by"
                onClear={isCustomSort
                  ? () => {
                      sharedSchedule?.setScheduleSort?.('default');
                      setSortMenuOpen(false);
                    }
                  : null
                }
              />
              {SCHEDULE_SORT_OPTIONS.map((opt) => (
                <RadioMenuItem
                  key={opt.key}
                  selected={sortKey === opt.key}
                  label={opt.label}
                  onClick={() => {
                    sharedSchedule?.setScheduleSort?.(opt.key);
                    setSortMenuOpen(false);
                  }}
                />
              ))}
              {isCustomSort && (
                <>
                  <PopoverDivider />
                  <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      sharedSchedule?.setScheduleSort?.('default');
                      setSortMenuOpen(false);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '7px 10px',
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--accent-text)',
                      cursor: 'pointer',
                      borderRadius: 5,
                      textAlign: 'left',
                      fontSize: 14, fontWeight: 500,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    Reset to default order
                  </button>
                </>
              )}
            </>
          );
        })()}
      </Popover>
    </div>
  );
};

export default TasksTab;
